import React, { useState, useEffect, useContext, createContext, useRef } from 'react';
import * as Sentry from "@sentry/react";
import { supabase } from '../lib/supabase';
import { mirrorSalesTaxReceipts, dropSalesTaxReceiptMirror } from '../lib/salesTaxReceiptMirror';
import { bulkDualWriteVendorRows } from '../lib/vendorDualWrite';
import { createBackup } from './DataProtection';
import { restateCommittedFinMarkup, resolveMarkupDefaults, itemToRow, rowToItem, guardedItemWrite, diffRowCols, generateItemNumber, rowToReceipt, receiptToRow, rowToInvoice, invoiceToRow, rowToRetainerEntry, retainerEntryToRow, rowToClientCredit, clientCreditToRow, rowToVendorCredit, vendorCreditToRow, rowToOwnerDraw, ownerDrawToRow, rowToStateTax, stateTaxToRow, rowToSalesTaxCode, salesTaxCodeToRow, rowToSalesTaxFiling, salesTaxFilingToRow, rowToSalesTaxFilingItem, salesTaxFilingItemToRow, rowToSalesTaxFilingFile, rowToStateTaxDocument, rowToFinalInvoiceItem, finTaxSnapshotRowToDb, rowToClientPayment, clientPaymentToRow, rowToPaymentAllocation, paymentAllocationToRow, rowToChangeOrder, changeOrderToRow, calcChangeOrderDelta, changeOrderClientIds, billingSummaryPeriodLabel, shouldApplyVendorReceiptCascade, buildVendorPaymentFromReceipt, findCascadePaymentRow, itemPatchesForReceiptCascade, poStatusPatchForReceiptCascade, VENDOR_RECEIPT_KINDS, resolveCascadeItems, allocateVendorPaymentToItems, rowToCreditCard, creditCardToRow, rowToPO, poToRow, vendorToRow, resolveVendorByName, rowToDocumentSend, buildDocumentSendInsert, rowToReceiptAttachment, rowToScheduleTemplate, scheduleTemplateToRow, rowToSchedule, scheduleToRow } from '../utils';

// ── CONSTANTS (duplicated here so context is self-contained) ─────────────────
var DEFAULT_VENDORS=[{id:1,name:"Holly Hunt",contact:"",email:"",phone:"",notes:"To the trade",vendorType:"Vendor"},{id:2,name:"Visual Comfort",contact:"",email:"",phone:"",notes:"",vendorType:"Vendor"},{id:3,name:"RH Contract",contact:"",email:"",phone:"",notes:"",vendorType:"Vendor"}];
// Project shape lives in app_storage as JSON (no SQL table). Each project
// clientId links to the clients directory.
var DEFAULT_PROJECTS=[{id:1,label:"26-Hoback",projectCode:"26HOB",address:"",address2:"",city:"",state:"",zip:"",attn:"",rooms:[],clientId:"",qbCustomerId:"",hasRetainer:false},{id:2,label:"26-Ptarmigan",projectCode:"26PTA",address:"",address2:"",city:"",state:"",zip:"",attn:"",rooms:[],clientId:"",qbCustomerId:"",hasRetainer:false},{id:3,label:"26-Moskito",projectCode:"26MOS",address:"",address2:"",city:"",state:"",zip:"",attn:"",rooms:[],clientId:"",qbCustomerId:"",hasRetainer:false}];
var LEGACY_SCHEDULE_MAP={"Paint":"Finish Schedule","Wallpaper":"Finish Schedule"};

// ── SUPABASE HELPERS ─────────────────────────────────────────────────────────
function storageGet(k){return supabase.from('app_storage').select('value').eq('key',k).maybeSingle().then(function(r){return (r&&r.data)?JSON.parse(r.data.value):null;});}
function storageSet(k,v){return supabase.from('app_storage').upsert({key:k,value:JSON.stringify(v)},{onConflict:'key'}).then(function(r){return !r.error;});}

function loadOrInit(k,d){return storageGet(k).then(function(s){
  if(s!==null&&s!==undefined)return s;
  var backup=localStorage.getItem(k+'_backup');
  if(backup){var parsed=JSON.parse(backup);if(parsed&&parsed.length>0){return parsed;}}
  return d;
});}

function localBackup(items){
  try{
    var raw=localStorage.getItem('csid_items_backup');
    var all=[];
    try{var parsed=JSON.parse(raw||'[]');all=Array.isArray(parsed)?parsed:[];}catch(e){all=[];}
    all=all.filter(function(x){return x&&x.id&&!/^\d+$/.test(String(x.id));});
    (items||[]).forEach(function(item){
      if(!item||!item.id||/^\d+$/.test(String(item.id)))return;
      var i=all.findIndex(function(x){return x.id===item.id;});
      if(i>=0)all[i]=item;else all.push(item);
    });
    localStorage.setItem('csid_items_backup',JSON.stringify(all));
    localStorage.setItem('csid_backup_time',new Date().toISOString());
  }catch(e){console.warn('localStorage backup failed',e);}
}


// itemToRow — imported from ../utils

// rowToItem — imported from ../utils

// ── SAVE QUEUE ───────────────────────────────────────────────────────────────
var _saveQueue={};
var _saveTimer=null;
var _saveInProgress=false;

// ── STALE-WRITE GUARD state ──────────────────────────────────────────────
// _itemRev: row id → the updated_at THIS session last saw for that row.
// _itemBase: row id → the itemToRow-normalized row as last known FROM the
// server (seeded at boot, advanced on every successful save). The base is
// what turns saves into minimal field-level patches: saveItemToSupabase
// diffs the outgoing row against it and writes only the changed columns.
//
// Concurrency model (v2, 2026-08-26): a save is conditional on updated_at
// (the items_updated_at trigger bumps it on every UPDATE). On a rev
// conflict the save AUTO-REBASES — fetch the fresh row; if the remote
// change touched DIFFERENT columns than ours, re-apply our patch on top
// of theirs and merge their change into local state (both edits survive,
// nobody sees an error). Only a same-column conflict blocks with the
// hard-refresh banner. Day-one v1 blocked on ANY remote touch of the row,
// which fired constantly in a two-user shop ("this isn't going to work").
// The 2026-08-24 CO-qty clobber is impossible under either version — v2
// simply resolves it silently and correctly (their status columns + our
// qty column both land). No rev known → legacy upsert (new-row INSERT
// path and the localStorage-backup-boot escape hatch).
var _itemRev={};
var _itemBase={};
// Set by AppProvider — folds a rebased merge (their columns + ours) back
// into React state so the UI reflects the remote edit without a refresh.
var _mergeRemoteItem=null;

function noteItemBaseRow(rawRow){
  if(rawRow&&rawRow.id)_itemBase[rawRow.id]=itemToRow(rowToItem(rawRow));
}

// Record the fresh rev for rows returned by any items write that chained
// .select('id,updated_at'). Exported so components doing targeted item
// updates (e.g. ItemForm image/hero saves) can keep this session's rev
// current — otherwise their own next full-row save would false-conflict.
export function noteItemRevRows(rows){
  (rows||[]).forEach(function(d){
    if(d&&d.id&&d.updated_at)_itemRev[d.id]=d.updated_at;
  });
}

// Re-fetch revs for rows changed server-side without a returning SELECT
// (e.g. the finalize_invoice_atomic RPC stamping final_invoice_id).
// Best-effort: on failure the affected items just conflict on their next
// save and ask for a refresh — safe, merely noisier.
async function refreshItemRevs(ids){
  if(!ids||!ids.length)return;
  try{
    var r=await supabase.from('items').select('*').in('id',ids);
    if(!r.error){
      noteItemRevRows(r.data);
      (r.data||[]).forEach(noteItemBaseRow);
    }
  }catch(e){console.warn('refreshItemRevs failed (non-critical):',e);}
}

async function saveItemToSupabase(item){
  var rowFull=itemToRow(item);
  var id=rowFull.id;
  var lastErr=null;
  var strippedCols=[];
  // Field-level patch: write only the columns this session changed,
  // diffed against the last-known server row. No base (new row, or a
  // pre-guard/backup-boot session) → full row, legacy behavior.
  var base=id?_itemBase[id]:null;
  var row;
  // Diagnostic (2026-09-02, the FOUNDATION MATTRESS PAD incident): capture
  // WHERE this save came from. Cimon hit a same-field conflict banner on an
  // item she reports she was not editing — meaning some code path saved an
  // item on her behalf with price columns in its patch. The stack trace +
  // patch detail in the console (and Sentry) is how we catch that path
  // red-handed on the next occurrence instead of theorizing.
  var _saveOrigin=new Error('saveItem call origin').stack;
  if(base){
    var patch=diffRowCols(base,rowFull);
    if(Object.keys(patch).length===0)return true; // nothing changed — no write
    row=Object.assign({id:id},patch);
  }else{
    row=rowFull;
  }
  var expected=id?_itemRev[id]:null;
  function conflictBanner(overlapCols){
    var name=(item&&(item.productName||item.item))||String(id);
    var what=(overlapCols&&overlapCols.length)
      ? ' Conflicting field'+(overlapCols.length>1?'s':'')+': '+overlapCols.join(', ')+'.'
      : '';
    var diag={
      itemId:id, itemName:name, overlap:overlapCols,
      patchCols:Object.keys(row).filter(function(k){return k!=='id';}),
      patchValues:{}, baseValues:{},
      origin:_saveOrigin,
    };
    (overlapCols||[]).forEach(function(k){
      diag.patchValues[k]=row[k];
      diag.baseValues[k]=base?base[k]:undefined;
    });
    console.error('saveItem BLOCKED — same-field conflict. FULL DIAGNOSTIC:',diag);
    try{Sentry.captureMessage('saveItem same-field conflict: '+name+' ('+id+') cols='+(overlapCols||[]).join(',')+' patchCols='+diag.patchCols.join(',')+' origin='+String(_saveOrigin).split('\n').slice(1,5).join(' | '),'warning');}catch(_e){}
    if(window._setSaveError)window._setSaveError('⚠ NOT saved: "'+name+'" changed in the database while this page held an older copy.'+what+' If you weren’t editing this item, Gilda saved it on your behalf — screenshot the browser Console (View → Developer → JavaScript Console) which now records exactly what tried to save. Then hard-refresh.');
    return false;
  }
  // Rev conflict → auto-rebase. Fetch the fresh row; when the remote
  // change touched only OTHER columns, re-apply our patch on top with the
  // fresh rev and fold their change into local state — both edits land,
  // no user friction. Same-column overlap is a real conflict → banner.
  async function rebase(){
    var fr=await supabase.from('items').select('*').eq('id',id).maybeSingle();
    if(fr.error)return null; // transport-ish — let retry loop handle
    if(!fr.data)return conflictBanner(null); // row gone (deleted remotely)
    var fresh=fr.data;
    if(!base)return conflictBanner(null); // no base → cannot prove disjoint
    var freshNorm=itemToRow(rowToItem(fresh));
    var remoteCols=Object.keys(diffRowCols(base,freshNorm));
    var overlap=Object.keys(row).filter(function(k){return k!=='id'&&remoteCols.indexOf(k)>=0;});
    if(overlap.length>0)return conflictBanner(overlap);
    var r2=await guardedItemWrite(supabase,row,fresh.updated_at);
    if(r2.outcome==='saved'){
      _itemRev[id]=r2.rev;
      _itemBase[id]=Object.assign({},freshNorm,row,{id:id});
      // Surface the remote columns into React state so the UI shows their
      // edit alongside ours without a refresh.
      if(_mergeRemoteItem)_mergeRemoteItem(rowToItem(Object.assign({},fresh,row)));
      console.warn('saveItem rebased "'+(item&&(item.productName||item.item)||id)+'": remote cols',remoteCols,'+ ours',Object.keys(row).filter(function(k){return k!=='id';}));
      return true;
    }
    if(r2.outcome==='conflict')return conflictBanner(null); // changed again mid-rebase — give up loudly
    return null;
  }
  function applyOutcome(res){
    if(res.outcome==='saved'){
      if(res.id&&res.rev)_itemRev[res.id]=res.rev;
      if(res.id)_itemBase[res.id]=Object.assign({},base||rowFull,row,{id:res.id});
      return true;
    }
    if(res.outcome==='conflict')return 'rebase';
    return null; // structured error — caller decides strip/retry
  }
  // Phase 1: strip unknown columns — each round removes one bad col, up to 20 cols
  for(var strip=0;strip<20;strip++){
    try{
      var r0=await guardedItemWrite(supabase,row,expected);
      var out0=applyOutcome(r0);
      if(out0==='rebase'){var rb0=await rebase();if(rb0!==null)return rb0;break;}
      if(out0!==null)return out0;
      lastErr=r0.error.message;
      if(r0.error.code==='PGRST204'||lastErr.includes('does not exist')){
        var cm=lastErr.match(/"([^"]+)" of relation/)||lastErr.match(/column "([^"]+)"/)||lastErr.match(/field "([^"]+)"/);
        if(cm&&row.hasOwnProperty(cm[1])){
          strippedCols.push(cm[1]);
          console.warn('saveItem: stripping unknown column "'+cm[1]+'" and retrying');
          delete row[cm[1]];
          continue; // strip next bad col
        }
      }
    }catch(e){lastErr=e.message;}
    break; // non-schema error — fall through to timing retries
  }
  // Phase 2: timing retries for transient network/server errors
  for(var attempt=1;attempt<=3;attempt++){
    try{
      var r=await guardedItemWrite(supabase,row,expected);
      var out=applyOutcome(r);
      if(out==='rebase'){var rb=await rebase();if(rb!==null)return rb;}
      else if(out!==null)return out;
      else lastErr=r.error.message;
      if(attempt<3)await new Promise(function(res){setTimeout(res,1000*attempt);});
    }catch(e){
      lastErr=e.message;
      if(attempt<3)await new Promise(function(res){setTimeout(res,1000*attempt);});
    }
  }
  console.error('saveItem FAILED after attempts:',lastErr,strippedCols.length?'(stripped cols: '+strippedCols.join(', ')+')':'');
  Sentry.captureMessage('saveItemToSupabase failed after retries: '+lastErr, 'error');
  if(window._setSaveError)window._setSaveError('⚠ Save failed: '+lastErr+'. Data backed up locally.');
  return false;
}

async function logChange(oldItem, newItem){
  try {
    var watchFields = [
      'item','status','vendor','roomName','roomNumber','schedule','category',
      'qty','qtyType','notes','leadTime','leadWeeks','poNumber','orderPlacedDate','receivedDate',
      // Tracker rebuild (2026-07-28): every physical-stage date + shipment
      // ref is audited, so stage stamps AND after-the-fact corrections
      // both land in change_history (the item's History tab) with
      // who/when. Cimon: "can these dates be logged into the system so
      // we know the history?" — yes, via this list.
      'shippedDate','deliveredDate','installPlannedDate','installDays','installEventId','installDate','installScheduleNotes',
      'trackingNumber','carrier','installer','statusNotes',
      'project','netPrice','markupPct','depositPct','fees',
    ];
    var entries = [];
    watchFields.forEach(function(field){
      var oldVal = JSON.stringify(oldItem[field]!=null?oldItem[field]:'');
      var newVal = JSON.stringify(newItem[field]!=null?newItem[field]:'');
      if(oldVal !== newVal){
        entries.push({
          item_id: newItem.id,
          item_name: newItem.item||'',
          project: newItem.project||newItem.projectLabel||'',
          field_changed: field,
          old_value: oldVal.slice(0,500),
          new_value: newVal.slice(0,500),
          changed_by: '',
        });
      }
    });
    if(entries.length === 0) return;
    supabase.auth.getUser().then(function(r){
      var email = r&&r.data&&r.data.user?r.data.user.email:'';
      entries.forEach(function(e){e.changed_by=email;});
      supabase.from('change_history').insert(entries).then(function(r){
        if(r.error) console.warn('History log failed (non-critical):', r.error.message);
      });
    });
  } catch(e) {
    console.warn('History log exception (non-critical):', e);
  }
}

async function flushSaveQueue(){
  var batch=Object.values(_saveQueue);
  _saveQueue={};_saveTimer=null;
  if(batch.length===0)return;
  _saveInProgress=true;
  // localStorage backup first
  try{
    var raw=localStorage.getItem('csid_items_backup');
    var all=[];
    try{var parsed=JSON.parse(raw||'[]');all=Array.isArray(parsed)?parsed:[];}catch(e){all=[];}
    all=all.filter(function(x){return x&&x.id&&!/^\d+$/.test(String(x.id));});
    batch.forEach(function(item){
      if(!item||!item.id||/^\d+$/.test(String(item.id)))return;
      var i=all.findIndex(function(x){return x.id===item.id;});
      if(i>=0)all[i]=item;else all.push(item);
    });
    localStorage.setItem('csid_items_backup',JSON.stringify(all));
    localStorage.setItem('csid_backup_time',new Date().toISOString());
  }catch(e){console.warn('localStorage backup failed',e);}
  // Then Supabase
  try{
    for(var i=0;i<batch.length;i++){
      if(batch[i]&&batch[i].id&&!/^\d+$/.test(String(batch[i].id))){
        await saveItemToSupabase(batch[i]);
      }
    }
  }finally{
    _saveInProgress=false;
  }
}

function queueSave(item){
  if(!item||!item.id)return;
  _saveQueue[item.id]=item;
  if(_saveTimer)clearTimeout(_saveTimer);
  _saveTimer=setTimeout(flushSaveQueue,600);
}

async function deleteItemFromSupabase(id){
  var r=await supabase.from('items').update({deleted:true,deleted_at:new Date().toISOString()}).eq('id',id).select('id,updated_at');
  if(!r.error)noteItemRevRows(r.data);
}

// ── PAGINATED LOADS ───────────────────────────────────────────────────────────
// Supabase/PostgREST caps every select at 1,000 rows and silently returns the
// first page — no error. Every list loader MUST page through .range() or rows
// past 1,000 never reach the app (the 2026-07-26 bug: items loaded
// created_at-ascending, so the NEWEST items were invisible app-wide, including
// search). buildQuery must return a FRESH builder on each call (builders are
// single-use). fetchAllRows appends an `id` tiebreaker after the caller's own
// .order() so ties on the sort key (bulk imports share one created_at) can't
// skip or duplicate rows across page boundaries — every table here has an id
// PK. Errors return the supabase result untouched so each loader's existing
// error handling (missing-table warns, localStorage fallback) still applies.
var LOAD_PAGE_SIZE=1000;
export async function fetchAllRows(buildQuery){
  var all=[];
  for(var from=0;;from+=LOAD_PAGE_SIZE){
    var r=await buildQuery().order('id',{ascending:true}).range(from,from+LOAD_PAGE_SIZE-1);
    if(r.error)return r;
    var rows=r.data||[];
    all=all.concat(rows);
    if(rows.length<LOAD_PAGE_SIZE)return {data:all,error:null};
  }
}

async function loadAssembliesFromSupabase(){
  var r=await fetchAllRows(function(){return supabase.from('assemblies').select('*').order('created_at',{ascending:true});});
  if(r.error){console.error('loadAssemblies error:',r.error);return [];}
  return r.data||[];
}

// ── FIRM SETTINGS (SQL TABLE) ─────────────────────────────────────────────────
// Single-row table since 2026-06-06. Replaces the app_storage.csid_firmsettings
// JSON blob, which was prone to whole-blob overwrite races (causing the
// "Pine Street default leaked into storage" + "Toggl workspace ID dropped on
// save" bugs). Per-column UPDATEs eliminate the race entirely.

var FIRM_SETTINGS_FIELD_MAP = {
  name:                          'name',
  address:                       'address',
  city:                          'city',
  state:                         'state',
  zip:                           'zip',
  phone:                         'phone',
  email:                         'email',
  sellerPermit:                  'seller_permit',
  principalName:                 'principal_name',
  principalTitle:                'principal_title',
  togglWorkspaceId:              'toggl_workspace_id',
  nonBillableTogglProjects:      'non_billable_toggl_projects',
  ownerEmail:                    'owner_email',
  qbRealmId:                     'qb_realm_id',
  qbEnvironment:                 'qb_environment',
  qbFeeIds:                      'qb_fee_ids',
  qbAccountIdRetainerLiability:  'qb_account_id_retainer_liability',
  studioTeam:                    'studio_team',
};

function rowToFirmSettings(row) {
  if (!row) return null;
  return {
    id:                            row.id,
    name:                          row.name || '',
    address:                       row.address || '',
    city:                          row.city || '',
    state:                         row.state || '',
    zip:                           row.zip || '',
    phone:                         row.phone || '',
    email:                         row.email || '',
    sellerPermit:                  row.seller_permit || '',
    principalName:                 row.principal_name || '',
    principalTitle:                row.principal_title || '',
    togglWorkspaceId:              row.toggl_workspace_id || '',
    nonBillableTogglProjects:      row.non_billable_toggl_projects || '',
    ownerEmail:                    row.owner_email || '',
    qbRealmId:                     row.qb_realm_id || '',
    qbEnvironment:                 row.qb_environment || '',
    qbFeeIds:                      row.qb_fee_ids || {},
    qbAccountIdRetainerLiability:  row.qb_account_id_retainer_liability || '',
    studioTeam:                    Array.isArray(row.studio_team) ? row.studio_team : [],
  };
}

async function loadFirmSettingsFromSupabase(){
  var r = await supabase.from('firm_settings').select('*').limit(1).maybeSingle();
  if (r.error) {
    console.error('loadFirmSettings error:', r.error);
    return null;
  }
  return rowToFirmSettings(r.data);
}

async function loadVendorPaymentsFromSupabase(){
  var r=await fetchAllRows(function(){return supabase.from('vendor_payments').select('*').order('payment_date',{ascending:false});});
  if(r.error){console.error('loadVendorPayments error:',r.error);return [];}
  return r.data||[];
}

async function loadVendorPaymentItemsFromSupabase(){
  // Phase 3 (2026-06-02). Consumer migration: buildFinalInvoicePayload
  // reads cc_fee_share off these rows in shadow mode (legacy renders by
  // default until VITE_FEATURE_VENDOR_PAYMENT_ITEMS_CC_FEE_READS=true).
  // Soft-fail to empty array on relation-not-found so the app stays up
  // if the Phase 0 migration hasn't been applied in a local environment.
  var r=await fetchAllRows(function(){return supabase.from('vendor_payment_items').select('*').order('created_at',{ascending:false});});
  if(r.error){
    var code=String(r.error.code||'');
    var msg=String(r.error.message||'');
    if(code==='PGRST205'||code==='42P01'||/does not exist|relation/i.test(msg)){
      console.warn('vendor_payment_items table not found — has the Phase 0 migration been run? (supabase/migrations/20260601_option_a_phase_0_schema.sql)');
      return [];
    }
    console.error('loadVendorPaymentItems error:',r.error);return [];
  }
  return r.data||[];
}

async function loadReceiptsFromSupabase(){
  var r=await fetchAllRows(function(){return supabase.from('receipts').select('*').order('created_at',{ascending:false});});
  if(r.error){console.error('loadReceipts error:',r.error);return [];}
  return (r.data||[]).map(rowToReceipt);
}

async function loadReceiptAttachmentsFromSupabase(){
  // Phase 4A (2026-06-27) — per-file attachments for receipts. Table is
  // populated by the receipt_attachments migration's backfill + any new
  // multi-file uploads (Phase 4B+). Until Phase 4B ships, the table holds
  // exactly one row per existing receipt (the primary 'invoice' attachment).
  // If the migration hasn't been applied yet, Supabase returns an error
  // about the missing relation — we log + return [] so the app stays up.
  var r = await fetchAllRows(function(){return supabase.from('receipt_attachments').select('*').order('uploaded_at',{ascending:true});});
  if (r.error) {
    // Code 42P01 = undefined table. Pre-migration; swallow.
    if (r.error.code !== '42P01') {
      console.error('loadReceiptAttachments error:', r.error);
    }
    return [];
  }
  return (r.data || []).map(rowToReceiptAttachment);
}

// Schedules rebuild Phase 1 (docs/SCHEDULES_REBUILD_SPEC.md §4.3). Both
// loaders tolerate the pre-migration state (missing relation → warn +
// empty) so this code can deploy before the operator runs
// supabase/migrations/20260706_create_schedule_tables.sql.
async function loadScheduleTemplatesFromSupabase(){
  var r=await fetchAllRows(function(){return supabase.from('schedule_templates').select('*').order('name',{ascending:true});});
  if(r.error){
    var code=String(r.error.code||'');
    var msg=String(r.error.message||'');
    if(code==='PGRST205'||code==='42P01'||/does not exist|relation/i.test(msg)){
      console.warn('schedule_templates table not found — has the migration been run? (supabase/migrations/20260706_create_schedule_tables.sql)');
      return [];
    }
    console.error('loadScheduleTemplates error:',r.error);return [];
  }
  return (r.data||[]).map(rowToScheduleTemplate);
}

async function loadSchedulesFromSupabase(){
  var r=await fetchAllRows(function(){return supabase.from('schedules').select('*').order('updated_at',{ascending:false});});
  if(r.error){
    var code=String(r.error.code||'');
    var msg=String(r.error.message||'');
    if(code==='PGRST205'||code==='42P01'||/does not exist|relation/i.test(msg)){
      console.warn('schedules table not found — has the migration been run? (supabase/migrations/20260706_create_schedule_tables.sql)');
      return [];
    }
    console.error('loadSchedules error:',r.error);return [];
  }
  return (r.data||[]).map(rowToSchedule);
}

async function loadFirmCreditCardsFromSupabase(){
  var r=await fetchAllRows(function(){return supabase.from('firm_credit_cards').select('*').order('label',{ascending:true});});
  if(r.error){console.error('loadFirmCreditCards error:',r.error);return [];}
  return (r.data||[]).map(rowToCreditCard);
}

async function loadInvoicesFromSupabase(){
  var r=await fetchAllRows(function(){return supabase.from('invoices').select('*').order('created_at',{ascending:false});});
  if(r.error){console.error('loadInvoices error:',r.error);return [];}
  return (r.data||[]).map(rowToInvoice);
}

async function loadClientPaymentsFromSupabase(){
  var r=await fetchAllRows(function(){return supabase.from('client_payments').select('*').order('payment_date',{ascending:false});});
  if(r.error){
    var code=String(r.error.code||'');
    var msg=String(r.error.message||'');
    if(code==='PGRST205'||code==='42P01'||/does not exist|relation/i.test(msg)){
      console.warn('client_payments table not found — has the migration been run? (supabase/migrations/20260429_create_client_payments.sql)');
      return [];
    }
    console.error('loadClientPayments error:',r.error);return [];
  }
  return (r.data||[]).map(rowToClientPayment);
}

async function loadChangeOrdersFromSupabase(){
  var r=await fetchAllRows(function(){return supabase.from('change_orders').select('*').order('filed_at',{ascending:false});});
  if(r.error){
    var code=String(r.error.code||'');
    var msg=String(r.error.message||'');
    if(code==='PGRST205'||code==='42P01'||/does not exist|relation/i.test(msg)){
      console.warn('change_orders table not found — has the migration been run? (supabase/migrations/20260502_create_change_orders.sql)');
      return [];
    }
    console.error('loadChangeOrders error:',r.error);return [];
  }
  return (r.data||[]).map(rowToChangeOrder);
}

// Phase 5A — versioned send history. See ARCHITECTURE.md §4 and
// supabase/migrations/20260624_create_document_sends.sql. Returns the
// full set sorted by created_at descending (newest first) so latest-send
// lookups and the Send History UI both read from a pre-sorted array.
async function loadDocumentSendsFromSupabase(){
  var r=await fetchAllRows(function(){return supabase.from('document_sends').select('*').order('created_at',{ascending:false});});
  if(r.error){
    var code=String(r.error.code||'');
    var msg=String(r.error.message||'');
    if(code==='PGRST205'||code==='42P01'||/does not exist|relation/i.test(msg)){
      console.warn('document_sends table not found — has the migration been run? (supabase/migrations/20260624_create_document_sends.sql)');
      return [];
    }
    console.error('loadDocumentSends error:',r.error);return [];
  }
  return (r.data||[]).map(rowToDocumentSend);
}

async function loadPaymentAllocationsFromSupabase(){
  var r=await fetchAllRows(function(){return supabase.from('payment_allocations').select('*').order('created_at',{ascending:false});});
  if(r.error){
    var code=String(r.error.code||'');
    var msg=String(r.error.message||'');
    if(code==='PGRST205'||code==='42P01'||/does not exist|relation/i.test(msg)){
      console.warn('payment_allocations table not found — has the migration been run? (supabase/migrations/20260429_create_client_payments.sql)');
      return [];
    }
    console.error('loadPaymentAllocations error:',r.error);return [];
  }
  return (r.data||[]).map(rowToPaymentAllocation);
}

async function loadRetainerLedgerFromSupabase(){
  var r=await fetchAllRows(function(){return supabase.from('retainer_ledger').select('*').order('entry_date',{ascending:false});});
  if(r.error){console.error('loadRetainerLedger error:',r.error);return [];}
  return (r.data||[]).map(rowToRetainerEntry);
}

async function loadClientCreditLedgerFromSupabase(){
  var r=await fetchAllRows(function(){return supabase.from('client_credit_ledger').select('*').order('entry_date',{ascending:false});});
  if(r.error){console.error('loadClientCreditLedger error:',r.error);return [];}
  return (r.data||[]).map(rowToClientCredit);
}

async function loadVendorCreditLedgerFromSupabase(){
  var r=await fetchAllRows(function(){return supabase.from('vendor_credit_ledger').select('*').order('entry_date',{ascending:false});});
  if(r.error){console.error('loadVendorCreditLedger error:',r.error);return [];}
  return (r.data||[]).map(rowToVendorCredit);
}

// Helper: detect "table does not exist" errors so missing-migration doesn't
// spam the console with 404s. Postgres returns 42P01 ("relation does not
// exist"); PostgREST returns 404 with code PGRST205 (no schema cache match).
function isMissingTableError(err) {
  if (!err) return false;
  return err.code === 'PGRST205' || err.code === '42P01' ||
         (typeof err.message === 'string' && err.message.indexOf('does not exist') >= 0);
}

async function loadOwnerDrawsFromSupabase(){
  var r=await fetchAllRows(function(){return supabase.from('owner_draws').select('*').order('draw_date',{ascending:false});});
  if(r.error){
    if(r.error.code==='PGRST301') return []; // RLS empty — non-owner/non-bookkeeper
    if(isMissingTableError(r.error)){
      console.warn('owner_draws table missing — run 20260426_create_owner_draws.sql in Supabase.');
      return [];
    }
    console.error('loadOwnerDraws error:',r.error);
    return [];
  }
  return (r.data||[]).map(rowToOwnerDraw);
}

// Non-owners cannot read state_tax_records (owner-only RLS — it holds
// portal passwords). The Sales Tax report still needs the list of
// registered states for its zero-return rows, so when the table comes
// back empty we read the credential-free `state_tax_registrations` view
// (20260913) instead. Rows from the view carry no username / password /
// portal URL; they feed the report only. StateTaxSection keeps its own
// owner gate and is unaffected.
async function loadStateTaxRegistrationsView(){
  var r=await fetchAllRows(function(){return supabase.from('state_tax_registrations').select('*').order('state_code',{ascending:true});});
  if(r.error){
    if(!isMissingTableError(r.error)) console.warn('state_tax_registrations view unavailable:',r.error.message||r.error);
    return [];
  }
  return (r.data||[]).map(rowToStateTax);
}

// final_invoice_items — per-item tax snapshot written by finalize_invoice_atomic
// at Save & Commit (2026-09-13). The Sales Tax report files from these rows.
// Soft-fail to [] if the Phase 0 table is missing locally.
async function loadFinalInvoiceItemsFromSupabase(){
  var r=await fetchAllRows(function(){return supabase.from('final_invoice_items').select('*').order('created_at',{ascending:false});});
  if(r.error){
    if(isMissingTableError(r.error)){ console.warn('final_invoice_items table missing — run 20260601_option_a_phase_0_schema.sql.'); return []; }
    console.error('loadFinalInvoiceItems error:',r.error);return [];
  }
  return (r.data||[]).map(rowToFinalInvoiceItem);
}

async function loadStateTaxRecordsFromSupabase(){
  var r=await fetchAllRows(function(){return supabase.from('state_tax_records').select('*').order('state_code',{ascending:true});});
  if(r.error){
    if(r.error.code==='PGRST301') return loadStateTaxRegistrationsView(); // RLS empty — staff/bookkeeper
    if(isMissingTableError(r.error)){
      console.warn('state_tax_records table missing — run 20260426_create_state_tax_records.sql in Supabase.');
      return [];
    }
    console.error('loadStateTaxRecords error:',r.error);
    return [];
  }
  // RLS returns an EMPTY result (not an error) for non-owners.
  if(!(r.data||[]).length) return loadStateTaxRegistrationsView();
  return (r.data||[]).map(rowToStateTax);
}

// sales_tax_codes — firm-level jurisdiction codes (2026-09-13). Readable by
// every authenticated user; soft-fail to [] until the migration runs.
async function loadSalesTaxCodesFromSupabase(){
  var r=await fetchAllRows(function(){return supabase.from('sales_tax_codes').select('*').order('code',{ascending:true});});
  if(r.error){
    if(isMissingTableError(r.error)){ console.warn('sales_tax_codes table missing — run 20260913_sales_tax_codes.sql.'); return []; }
    console.error('loadSalesTaxCodes error:',r.error);return [];
  }
  return (r.data||[]).map(rowToSalesTaxCode);
}

// sales_tax_filings — what was filed/paid per state per period (2026-09-20).
// Admin-only RLS; soft-fail to [] until the migration runs.
async function loadSalesTaxFilingsFromSupabase(){
  var r=await fetchAllRows(function(){return supabase.from('sales_tax_filings').select('*').order('period_key',{ascending:false});});
  if(r.error){
    if(isMissingTableError(r.error)){ console.warn('sales_tax_filings table missing — run 20260920_sales_tax_filings.sql.'); return []; }
    console.error('loadSalesTaxFilings error:',r.error);return [];
  }
  return (r.data||[]).map(rowToSalesTaxFiling);
}

// sales_tax_filing_items + sales_tax_filing_files (2026-10-15) — which
// items a filing stamped (tax frozen at save) and the portal PDFs behind
// it. Admin-only RLS; soft-fail to [] until the migration runs.
async function loadSalesTaxFilingItemsFromSupabase(){
  var r=await fetchAllRows(function(){return supabase.from('sales_tax_filing_items').select('*').order('created_at',{ascending:true});});
  if(r.error){
    if(isMissingTableError(r.error)){ console.warn('sales_tax_filing_items table missing — run 20261015_sales_tax_filing_items_files.sql.'); return []; }
    console.error('loadSalesTaxFilingItems error:',r.error);return [];
  }
  return (r.data||[]).map(rowToSalesTaxFilingItem);
}
async function loadSalesTaxFilingFilesFromSupabase(){
  var r=await fetchAllRows(function(){return supabase.from('sales_tax_filing_files').select('*').order('uploaded_at',{ascending:true});});
  if(r.error){
    if(isMissingTableError(r.error)){ console.warn('sales_tax_filing_files table missing — run 20261015_sales_tax_filing_items_files.sql.'); return []; }
    console.error('loadSalesTaxFilingFiles error:',r.error);return [];
  }
  return (r.data||[]).map(rowToSalesTaxFilingFile);
}
// state_tax_documents (2026-10-16) — resale certificates / licenses per
// state record. Owner + admin RLS; soft-fail to [] until the migration runs.
async function loadStateTaxDocumentsFromSupabase(){
  var r=await fetchAllRows(function(){return supabase.from('state_tax_documents').select('*').order('uploaded_at',{ascending:true});});
  if(r.error){
    if(isMissingTableError(r.error)){ console.warn('state_tax_documents table missing — run 20261016_state_tax_documents.sql.'); return []; }
    if(r.error.code==='PGRST301') return [];
    console.error('loadStateTaxDocuments error:',r.error);return [];
  }
  return (r.data||[]).map(rowToStateTaxDocument);
}

async function loadItemsFromSupabase(){
  var r=await fetchAllRows(function(){return supabase.from('items').select('*').is('deleted_at',null).order('created_at',{ascending:true});});
  if(r.error){
    // True error path -- network failure, RLS reject, etc. localStorage is
    // a legitimate fallback here because the server failed to respond.
    console.error('loadItems error:',r.error);
    var backup=localStorage.getItem('csid_items_backup');
    if(backup){
      var parsed=JSON.parse(backup);
      var validItems=Array.isArray(parsed)?parsed.filter(function(it){return it&&it.id&&it.item;}):(parsed&&parsed.items?parsed.items:[]);
      if(validItems.length>0){
        console.warn('loadItems falling back to localStorage backup ('+validItems.length+' items) due to Supabase error');
        try { Sentry.captureMessage('loadItems fallback to localStorage', 'warning'); } catch(_e) {}
        // Mark the global so the UI can warn the user that they're
        // looking at potentially-stale local data, not server truth.
        try { window._loadedFromBackup = true; } catch(_e) {}
        return validItems;
      }
    }
    return [];
  }
  // Supabase returned successfully with 0 items. **Do NOT auto-fall-back
  // to localStorage here** -- a legitimate empty result (fresh project,
  // RLS-filtered for this user) is indistinguishable from "stale local
  // data after a delete." Falling back would surface deleted items as if
  // they still existed, and a single edit afterward would silently
  // re-create them in the DB. Trust the server.
  if(!r.data){
    return [];
  }
  // Best-effort localStorage backup — Supabase is the source of truth, this
  // is just a safety net. Quota exceeded is non-fatal.
  try {
    localStorage.setItem('csid_items_backup', JSON.stringify(r.data.map(rowToItem)));
    localStorage.setItem('csid_backup_time', new Date().toISOString());
  } catch(e) {
    if (e && e.name === 'QuotaExceededError') {
      console.warn('localStorage quota exceeded — backup skipped. Data is safe in Supabase.');
      // Try to free space by removing the oldest backup snapshot
      try { localStorage.removeItem('csid_backup_history'); } catch(e2) {}
    } else {
      console.warn('localStorage backup failed:', e);
    }
  }
  // Stale-write guard: remember the updated_at + normalized base row we
  // loaded for every item — the base is what field-level save diffs are
  // computed against. (The localStorage-backup fallback path above
  // intentionally seeds nothing — backup items carry no rev/base, so
  // their saves fall back to the unguarded full-row upsert rather than
  // being unable to save at all.)
  noteItemRevRows(r.data);
  r.data.forEach(noteItemBaseRow);
  return r.data.map(rowToItem);
}

function checkAutoBackup(){
  try{
    var last=localStorage.getItem('csid_last_export');
    var sevenDays=7*24*60*60*1000;
    return !last||(Date.now()-parseInt(last))>sevenDays;
  }catch(e){return false;}
}

// ── CONTEXT ──────────────────────────────────────────────────────────────────
var AppContext=createContext(null);

export function useAppContext(){
  var ctx=useContext(AppContext);
  if(!ctx)throw new Error('useAppContext must be used within AppProvider');
  return ctx;
}

export function AppProvider({children}){
  var is=useState([]);var items=is[0];var setItems=is[1];
  // itemsRef — always-fresh mirror of the items array, updated EAGERLY by
  // updateItem/addItem (before React re-renders) and re-synced after every
  // commit. Exists because updateItem must merge its patch into the LATEST
  // item state, not the render-closure `items` snapshot. The 2026-07-08
  // SKU-loss bug: approveProposal ran several updateItem calls in one
  // async pass — the SKU+status save merged fine, but the follow-up
  // clientDepositInvoiceId tag merged into the PRE-approval closure
  // snapshot and its full-row save clobbered item_number (and status)
  // back to pre-approval values in the DB. Guard:
  // __tests__/updateItemStaleClosure.test.jsx.
  var itemsRef=useRef([]);
  useEffect(function(){ itemsRef.current=items; },[items]);
  // Rebase hook for saveItemToSupabase (module scope, no state access):
  // after a save auto-rebases over a remote edit, fold the merged item
  // (their columns + ours) back into React state.
  useEffect(function(){
    _mergeRemoteItem=function(mergedItem){
      if(!mergedItem||!mergedItem.id)return;
      itemsRef.current=(itemsRef.current||[]).map(function(it){return it.id===mergedItem.id?Object.assign({},it,mergedItem):it;});
      setItems(function(prev){return prev.map(function(it){return it.id===mergedItem.id?Object.assign({},it,mergedItem):it;});});
    };
    return function(){ _mergeRemoteItem=null; };
  },[]);
  var vs=useState(DEFAULT_VENDORS);var vendors=vs[0];var setVendors=vs[1];
  var ps=useState(DEFAULT_PROJECTS);var projects=ps[0];var setProjects=ps[1];
  var cls=useState([]);var clients=cls[0];var setClients=cls[1];
  // firmSettings default — intentionally empty. Real values load from
  // app_storage.csid_firmsettings (or eventually the firm_settings SQL
  // table). Pre-2026-06-06 this default held a fabricated address
  // ("340 Pine Street, Suite 302") that leaked into PDFs and QB push
  // payloads when an autosave race fired before app_storage data
  // arrived — see FOLLOWUPS.md "AppContext + adminTabs default firm
  // address corruption risk." Document data integrity (CLAUDE.md
  // Architecture Principles) requires real source values or empty,
  // never plausible-looking placeholders. Form placeholders are fine
  // (they're visual only); persisted defaults are not.
  var fss=useState({
    name:"",address:"",
    city:"",state:"",zip:"",phone:"",
    email:"",
    sellerPermit:"",principalName:"",
    principalTitle:""
  });
  var firmSettings=fss[0];var setFirmSettings=fss[1];
  var rs=useState(false);var ready=rs[0];var setReady=rs[1];
  var ues=useState("");var userEmail=ues[0];var setUserEmail=ues[1];
  var sev=useState("");var saveError=sev[0];var setSaveError=sev[1];
  var sli=useState(false);var savedI=sli[0];var setSavedI=sli[1];
  var slv=useState(false);var savedV=slv[0];var setSavedV=slv[1];
  var slp=useState(false);var savedP=slp[0];var setSavedP=slp[1];
  var slc=useState(false);var savedC=slc[0];var setSavedC=slc[1];
  var slfs=useState(false);var savedFS=slfs[0];var setSavedFS=slfs[1];
  var prps=useState([]);var proposals=prps[0];var setProposals=prps[1];
  var pos=useState([]);var purchaseOrders=pos[0];var setPurchaseOrders=pos[1];
  var vps=useState([]);var vendorPayments=vps[0];var setVendorPayments=vps[1];
  var vpis=useState([]);var vendorPaymentItems=vpis[0];var setVendorPaymentItems=vpis[1];
  var asm=useState([]);var assemblies=asm[0];var setAssemblies=asm[1];
  var rcs=useState([]);var receipts=rcs[0];var setReceipts=rcs[1];
  var fccs=useState([]);var firmCreditCards=fccs[0];var setFirmCreditCards=fccs[1];
  var ivs=useState([]);var invoices=ivs[0];var setInvoices=ivs[1];
  var rls=useState([]);var retainerLedger=rls[0];var setRetainerLedger=rls[1];
  var ccs=useState([]);var clientCreditLedger=ccs[0];var setClientCreditLedger=ccs[1];
  var vcs=useState([]);var vendorCreditLedger=vcs[0];var setVendorCreditLedger=vcs[1];
  var ods=useState([]);var ownerDraws=ods[0];var setOwnerDraws=ods[1];
  var sts=useState([]);var stateTaxRecords=sts[0];var setStateTaxRecords=sts[1];
  var stcs=useState([]);var salesTaxCodes=stcs[0];var setSalesTaxCodes=stcs[1];
  var stfs=useState([]);var salesTaxFilings=stfs[0];var setSalesTaxFilings=stfs[1];
  var stfis=useState([]);var salesTaxFilingItems=stfis[0];var setSalesTaxFilingItems=stfis[1];
  var stffs=useState([]);var salesTaxFilingFiles=stffs[0];var setSalesTaxFilingFiles=stffs[1];
  var stds=useState([]);var stateTaxDocuments=stds[0];var setStateTaxDocuments=stds[1];
  var fiis=useState([]);var finalInvoiceItems=fiis[0];var setFinalInvoiceItems=fiis[1];
  var cps=useState([]);var clientPayments=cps[0];var setClientPayments=cps[1];
  var pas=useState([]);var paymentAllocations=pas[0];var setPaymentAllocations=pas[1];
  var cos=useState([]);var changeOrders=cos[0];var setChangeOrders=cos[1];
  // Phase 5A — document_sends (versioned send history). See
  // ARCHITECTURE.md §4 + supabase/migrations/20260624_create_document_sends.sql.
  var dsts=useState([]);var documentSends=dsts[0];var setDocumentSends=dsts[1];
  // Phase 4A (2026-06-27) — receipt_attachments. Per-file attachments
  // for receipts. See supabase/migrations/20260627_create_receipt_
  // attachments_table.sql. Until Phase 4B UI ships, this state is
  // populated but not surfaced anywhere — the backfill creates one
  // primary attachment per existing receipt for future UI consumption.
  var rats=useState([]);var receiptAttachments=rats[0];var setReceiptAttachments=rats[1];
  // Schedules rebuild Phase 1 — schedule_templates + schedules (see
  // docs/SCHEDULES_REBUILD_SPEC.md §4.3 + migration
  // 20260706_create_schedule_tables.sql). Loaded at boot; no UI consumes
  // them until Phase 2/3 — the legacy SchedulesTab stays on its own path.
  var stpl=useState([]);var scheduleTemplates=stpl[0];var setScheduleTemplates=stpl[1];
  var schs=useState([]);var schedules=schs[0];var setSchedules=schs[1];
  var sids=useState(function(){return new Set();});var selectedItemIds=sids[0];var setSelectedItemIds=sids[1];
  var smd=useState(false);var selectionMode=smd[0];var setSelectionMode=smd[1];

  var isOwner=(userEmail||"").toLowerCase()==="c@cimonstarr.com";

  // Register window globals for save error + unload guard
  useEffect(function(){
    window._setSaveError=setSaveError;
    function handleUnload(e){
      if(Object.keys(_saveQueue).length>0||_saveTimer||_saveInProgress){
        e.preventDefault();
        e.returnValue='You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    }
    window.addEventListener('beforeunload',handleUnload);
    return function(){
      window._setSaveError=null;
      window.removeEventListener('beforeunload',handleUnload);
    };
  },[setSaveError]);

  // Auto-backup every 2 minutes
  useEffect(function(){
    if(!ready)return;
    var interval=setInterval(function(){
      try{
        if(items&&items.length>0){
          localStorage.setItem('csid_items_backup',JSON.stringify(items));
          localStorage.setItem('csid_backup_time',new Date().toISOString());
          console.log('Auto-backup: '+items.length+' items saved to localStorage');
        }
        if(vendors&&vendors.length>0)localStorage.setItem('csid_vendors_backup',JSON.stringify(vendors));
        if(projects&&projects.length>0)localStorage.setItem('csid_projects_backup',JSON.stringify(projects));
      }catch(e){console.warn('Auto-backup failed',e);}
    },120000);
    return function(){clearInterval(interval);};
  },[ready,items,vendors,projects]);

  // Initial data load
  useEffect(function(){
    supabase.auth.getSession().then(function(r){
      var email=r&&r.data&&r.data.session&&r.data.session.user?r.data.session.user.email:"";
      setUserEmail(email||"");
    });
    supabase.from('items').select('schedule').limit(1).then(function(r){
      if(r.error&&r.error.message&&r.error.message.includes('schedule')){
        if(window._setSaveError)window._setSaveError('⚠ Database needs migration.');
      }
    });
    Promise.all([
      loadItemsFromSupabase(),
      loadOrInit("csid_vendors",DEFAULT_VENDORS),
      loadOrInit("csid_projects",DEFAULT_PROJECTS),
      loadOrInit("csid_clients",[]),
      // firmSettings: SQL table since 2026-06-06 (was app_storage JSON blob).
      // Single-row table; loadFirmSettingsFromSupabase returns null if empty,
      // which means the migration's seed INSERT was skipped (data missing
      // upstream) — surface in Sentry. Should always return a populated row
      // in normal operation.
      loadFirmSettingsFromSupabase(),
      loadAssembliesFromSupabase(),
      loadProposalsFromSupabase(),
      loadPOsFromSupabase(),
      loadVendorPaymentsFromSupabase(),
      loadVendorPaymentItemsFromSupabase(),
      loadReceiptsFromSupabase(),
      loadInvoicesFromSupabase(),
      loadRetainerLedgerFromSupabase(),
      loadClientCreditLedgerFromSupabase(),
      loadVendorCreditLedgerFromSupabase(),
      loadOwnerDrawsFromSupabase(),
      loadStateTaxRecordsFromSupabase(),
      loadClientPaymentsFromSupabase(),
      loadPaymentAllocationsFromSupabase(),
      loadChangeOrdersFromSupabase(),
      loadFirmCreditCardsFromSupabase(),
      // Phase 5A — versioned send history. Appended at the tail so
      // earlier result indices stay stable; results[21].
      loadDocumentSendsFromSupabase(),
      // Phase 4A (2026-06-27) — receipt_attachments. Appended after
      // documentSends so earlier indices stay stable; results[22].
      loadReceiptAttachmentsFromSupabase(),
      // Schedules rebuild Phase 1 — appended at the tail so earlier
      // indices stay stable; results[23] + results[24].
      loadScheduleTemplatesFromSupabase(),
      loadSchedulesFromSupabase(),
      // FIN tax snapshot (2026-09-13) — appended at the tail; results[25].
      loadFinalInvoiceItemsFromSupabase(),
      // Sales tax codes (2026-09-13) — appended at the tail; results[26].
      loadSalesTaxCodesFromSupabase(),
      // Sales tax filings (2026-09-20) — appended at the tail; results[27].
      loadSalesTaxFilingsFromSupabase(),
      // Sales tax filing items + files (2026-10-15); results[28] + results[29].
      loadSalesTaxFilingItemsFromSupabase(),
      loadSalesTaxFilingFilesFromSupabase(),
      // State tax documents (2026-10-16); results[30].
      loadStateTaxDocumentsFromSupabase(),
    ]).then(function(results){
      if(!results[0]||results[0].length===0){
        // Server returned 0 items. **Do NOT silently auto-restore from
        // legacy app_storage `csid_items` or the local backup file.**
        // Both used to auto-rehydrate, and either could be stale
        // (post-delete or pre-migration). The risk is resurrection: a
        // bookkeeper deletes 20 items, page reloads, stale local copy
        // hydrates, every save re-creates the deleted items in the DB.
        // Trust the server. Surface a banner if local data exists so the
        // owner can manually choose to recover.
        var legacyHas = false, backupHas = false;
        try {
          var legacyKey = localStorage.getItem('csid_items_backup');
          if (legacyKey) {
            var parsedBackup = JSON.parse(legacyKey);
            backupHas = Array.isArray(parsedBackup) && parsedBackup.length > 0;
          }
        } catch (_e) {}
        loadOrInit("csid_items",[]).then(function(legacy){
          legacyHas = !!(legacy && legacy.length > 0);
          if (legacyHas || backupHas) {
            console.warn('Server returned 0 items but local copies exist (legacy app_storage: ' + legacyHas + ', localStorage backup: ' + backupHas + '). NOT auto-restoring -- trust server. Owner can recover via Data Protection → Restore.');
            try { Sentry.captureMessage('loadItems empty server with local backup present', 'warning'); } catch(_e) {}
            try { window._loadItemsServerEmpty = true; } catch(_e) {}
          }
          setItems([]);
        });
      } else {
        var migrated=results[0].map(function(it){
          if(LEGACY_SCHEDULE_MAP[it.schedule]){
            return Object.assign({},it,{schedule:LEGACY_SCHEDULE_MAP[it.schedule],category:LEGACY_SCHEDULE_MAP[it.schedule]});
          }
          return it;
        });
        var migrated2=migrated.map(function(it){
          var patch={};
          var newSpecs=Object.assign({},it.specs||{});
          if(newSpecs.tag&&!it.tag){patch.tag=newSpecs.tag;delete newSpecs.tag;}
          if(newSpecs.sourceUrl&&!it.sourceUrl){patch.sourceUrl=newSpecs.sourceUrl;delete newSpecs.sourceUrl;}
          if(Object.keys(patch).length>0)return Object.assign({},it,patch,{specs:newSpecs});
          return it;
        });
        setItems(migrated2);
        if(checkAutoBackup())window._exportDue=true;
      }
      var loadedVendors=results[1]||[];
      if(loadedVendors.length===0){
        try{
          var vbk=localStorage.getItem('csid_vendors_backup');
          if(vbk){var vbkParsed=JSON.parse(vbk);if(vbkParsed&&vbkParsed.length>0){loadedVendors=vbkParsed;console.warn('csid_vendors empty in Supabase — restored from localStorage backup');}}
        }catch(e){}
      }
      setVendors(loadedVendors);
      var loadedProjects=results[2]||[];
      if(loadedProjects.length===0){
        try{
          var pbk=localStorage.getItem('csid_projects_backup');
          if(pbk){var pbkParsed=JSON.parse(pbk);if(pbkParsed&&pbkParsed.length>0){loadedProjects=pbkParsed;console.warn('csid_projects empty in Supabase — restored from localStorage backup');}}
        }catch(e){}
      }
      setProjects(loadedProjects);
      var loadedClients=results[3]||[];
      if(loadedClients.length===0){
        try{
          var cbk=localStorage.getItem('csid_clients_backup');
          if(cbk){var cbkParsed=JSON.parse(cbk);if(cbkParsed&&cbkParsed.length>0){loadedClients=cbkParsed;console.warn('csid_clients empty in Supabase — restored from localStorage backup');}}
        }catch(e){}
      }
      setClients(loadedClients);
      if(results[4])setFirmSettings(results[4]);
      setAssemblies(results[5]||[]);
      setProposals(results[6]||[]);
      setPurchaseOrders(results[7]||[]);
      setVendorPayments(results[8]||[]);
      // results[9] = vendorPaymentItems (Phase 3, 2026-06-02). All
      // downstream indices shifted +1 from the prior list.
      setVendorPaymentItems(results[9]||[]);
      setReceipts(results[10]||[]);
      setInvoices(results[11]||[]);
      setRetainerLedger(results[12]||[]);
      setClientCreditLedger(results[13]||[]);
      setVendorCreditLedger(results[14]||[]);
      setOwnerDraws(results[15]||[]);
      setStateTaxRecords(results[16]||[]);
      setClientPayments(results[17]||[]);
      setPaymentAllocations(results[18]||[]);
      setChangeOrders(results[19]||[]);
      setFirmCreditCards(results[20]||[]);
      // Phase 5A — versioned send history. results[21].
      setDocumentSends(results[21]||[]);
      // Phase 4A — receipt_attachments. results[22].
      setReceiptAttachments(results[22]||[]);
      // Schedules rebuild Phase 1. results[23] + results[24].
      setScheduleTemplates(results[23]||[]);
      setSchedules(results[24]||[]);
      // FIN tax snapshot. results[25].
      setFinalInvoiceItems(results[25]||[]);
      // Sales tax codes. results[26].
      setSalesTaxCodes(results[26]||[]);
      // Sales tax filings. results[27].
      setSalesTaxFilings(results[27]||[]);
      // Sales tax filing items + files. results[28] + results[29].
      setSalesTaxFilingItems(results[28]||[]);
      setSalesTaxFilingFiles(results[29]||[]);
      setStateTaxDocuments(results[30]||[]);
      setReady(true);
    });
  },[]);

  // Save items to localStorage backup on every change
  useEffect(function(){
    if(!ready)return;
    localBackup(items);
    createBackup(items,vendors,projects,"auto");
    setSavedI(true);
    setSaveError("");
    setTimeout(function(){setSavedI(false);},1500);
  },[items]);

  // Save vendors/projects/clients/firmSettings on change
  useEffect(function(){
    if(!ready)return;
    try{localStorage.setItem('csid_vendors_backup',JSON.stringify(vendors));}catch(e){}
    storageSet("csid_vendors",vendors).then(function(ok){
      if(ok){
        setSavedV(true);setTimeout(function(){setSavedV(false);},1500);
      } else {
        console.error('storageSet csid_vendors failed — vendor data not persisted to Supabase');
        Sentry.captureMessage('storageSet csid_vendors failed','error');
      }
    });
  },[vendors]);
  useEffect(function(){
    if(!ready)return;
    try{localStorage.setItem('csid_projects_backup',JSON.stringify(projects));}catch(e){}
    storageSet("csid_projects",projects).then(function(ok){
      if(ok){
        setSavedP(true);setTimeout(function(){setSavedP(false);},1500);
      } else {
        console.error('storageSet csid_projects failed — project data not persisted to Supabase');
        Sentry.captureMessage('storageSet csid_projects failed','error');
      }
    });
  },[projects]);
  useEffect(function(){
    if(!ready)return;
    try{localStorage.setItem('csid_clients_backup',JSON.stringify(clients));}catch(e){}
    storageSet("csid_clients",clients).then(function(ok){
      if(ok){
        setSavedC(true);setTimeout(function(){setSavedC(false);},1500);
      } else {
        console.error('storageSet csid_clients failed — client data not persisted to Supabase');
        Sentry.captureMessage('storageSet csid_clients failed','error');
      }
    });
  },[clients]);
  // firmSettings no longer auto-saves the whole blob — saveFirmSettingsPatch
  // (below) updates only the fields in the patch via SQL UPDATE. This kills
  // the whole-blob overwrite race that caused the "Pine Street default leaked
  // into storage" + "Toggl Workspace ID dropped on save" bugs (2026-06-06).
  // Proposals now persist to Supabase via saveProposalToDb — no app_storage sync needed.

  // ── FIRM SETTINGS — PER-FIELD UPDATE ────────────────────────────────────
  // Each input on FirmSettings / SettingsPanel calls saveFirmSettingsPatch
  // with only the changed field(s). UPDATE writes those columns; other
  // columns untouched. No whole-blob snapshots; no race condition.
  // Returns Promise<{ ok: boolean, error?: string }> for caller feedback.
  async function saveFirmSettingsPatch(patch) {
    if (!patch || typeof patch !== 'object') return { ok: false, error: 'invalid patch' };
    var row = {};
    Object.keys(patch).forEach(function(k) {
      var sqlCol = FIRM_SETTINGS_FIELD_MAP[k];
      if (sqlCol) row[sqlCol] = patch[k];
    });
    if (Object.keys(row).length === 0) return { ok: true };  // nothing mapped; no-op
    var firmId = firmSettings && firmSettings.id;
    if (!firmId) {
      console.error('saveFirmSettingsPatch: firmSettings.id missing — table may be empty');
      Sentry.captureMessage('saveFirmSettingsPatch: missing firmSettings.id', 'error');
      return { ok: false, error: 'firm_settings row missing' };
    }
    var r = await supabase.from('firm_settings').update(row).eq('id', firmId).select().maybeSingle();
    if (r.error) {
      console.error('saveFirmSettingsPatch error:', r.error);
      Sentry.captureMessage('saveFirmSettingsPatch failed: ' + r.error.message, 'error');
      return { ok: false, error: r.error.message };
    }
    // Optimistically merge into React state from the returned row.
    if (r.data) {
      var merged = rowToFirmSettings(r.data);
      setFirmSettings(merged);
    }
    setSavedFS(true); setTimeout(function() { setSavedFS(false); }, 1500);
    return { ok: true };
  }

  // ── PROPOSAL SUPABASE OPERATIONS ────────────────────────────────────────
  function rowToProposal(row){
    var ids=row.item_ids;
    if(typeof ids==='string'){try{ids=JSON.parse(ids);}catch(e){ids=[];}}
    return{
      id:row.id,
      proposal_ref:row.proposal_ref||'',
      proposal_name:row.proposal_name||'',
      project:row.project||'',
      clientId:row.client_id||'',
      status:row.status||'Draft',
      items:Array.isArray(ids)?ids:[],
      total_net:row.total_net||0,
      total_client:row.total_client||0,
      total_deposit:row.total_deposit||0,
      depositPct:row.deposit_pct||80,
      notes:row.notes||'',
      version:row.version||1,
      created_at:row.created_at||'',
      sent_at:row.sent_at||null,
      approved_at:row.approved_at||null,
      lastEditedAt:row.last_edited_at||null,
      sentVia:row.sent_via||null,
      sentOffCycleAt:row.sent_off_cycle_at||null,
      billedOnSummaryId:row.billed_on_summary_id||'',
      // Phase 5 / QB readiness — populated when QB push fires
      qbEstimateId:        row.qb_estimate_id        || '',
      qbDepositInvoiceId:  row.qb_deposit_invoice_id || '',
      qbFinalInvoiceId:    row.qb_final_invoice_id   || '',
    };
  }
  async function loadProposalsFromSupabase(){
    var r=await fetchAllRows(function(){return supabase.from('proposals').select('*').order('created_at',{ascending:false});});
    if(r.error){console.error('loadProposals error:',r.error);return[];}
    return(r.data||[]).map(rowToProposal);
  }
  async function saveProposalToDb(p){
    var row={
      id:p.id,
      proposal_ref:p.proposal_ref||'',
      proposal_name:p.proposal_name||'',
      project:p.project||'',
      client_id:p.clientId||'',
      status:p.status||'Draft',
      item_ids:p.items||[],
      total_net:parseFloat(p.total_net)||0,
      total_client:parseFloat(p.total_client)||0,
      total_deposit:parseFloat(p.total_deposit)||0,
      deposit_pct:parseInt(p.depositPct)||80,
      notes:p.notes||'',
      version:p.version||1,
      created_at:p.created_at||new Date().toISOString(),
      sent_at:p.sent_at||null,
      approved_at:p.approved_at||null,
      last_edited_at:p.lastEditedAt||null,
      sent_via:p.sentVia||null,
      sent_off_cycle_at:p.sentOffCycleAt||null,
      billed_on_summary_id:p.billedOnSummaryId||null,
      // Phase 5 / QB
      qb_estimate_id:        p.qbEstimateId        || '',
      qb_deposit_invoice_id: p.qbDepositInvoiceId  || '',
      qb_final_invoice_id:   p.qbFinalInvoiceId    || '',
    };
    var lastErr=null;
    for(var attempt=1;attempt<=6;attempt++){
      try{
        var r=await supabase.from('proposals').upsert(row,{onConflict:'id'});
        if(!r.error)return true;
        lastErr=r.error.message;
        if(r.error.code==='PGRST204'||lastErr.includes('does not exist')){
          var colMatch=lastErr.match(/"([^"]+)" of relation/)||lastErr.match(/column "([^"]+)"/);
          if(colMatch&&row.hasOwnProperty(colMatch[1])){
            console.warn('saveProposal: stripping unknown column "'+colMatch[1]+'" and retrying');
            delete row[colMatch[1]];
            continue;
          }
        }
        if(attempt<3)await new Promise(function(res){setTimeout(res,1000*attempt);});
      }catch(e){
        lastErr=e.message;
        if(attempt<3)await new Promise(function(res){setTimeout(res,1000*attempt);});
      }
    }
    console.error('saveProposalToDb FAILED:',lastErr);
    Sentry.captureMessage('saveProposalToDb failed after retries: '+lastErr,'error');
    if(window._setSaveError)window._setSaveError('⚠ Proposal save failed: '+lastErr);
    return false;
  }
  // Field-level patch for proposal metadata (Edit Details). Writes ONLY the
  // columns passed — never item_ids/status/totals — so a save from a tab
  // whose proposal copy is stale cannot wipe the item list the way the
  // full-row upsert did to 26HOB-P-069 on 2026-10-05.
  async function patchProposalInDb(id, patch){
    var map={proposal_ref:'proposal_ref',proposal_name:'proposal_name',project:'project',
      clientId:'client_id',notes:'notes',depositPct:'deposit_pct',lastEditedAt:'last_edited_at'};
    var row={};
    Object.keys(patch||{}).forEach(function(k){ if(map[k]) row[map[k]]=patch[k]; });
    if(!Object.keys(row).length) return true;
    var r=await supabase.from('proposals').update(row).eq('id',id);
    if(r.error){ console.error('patchProposalInDb FAILED:',r.error.message); Sentry.captureMessage('patchProposalInDb failed: '+r.error.message,'error'); return false; }
    return true;
  }
  async function deleteProposalFromDb(id){
    var r=await supabase.from('proposals').delete().eq('id',id);
    if(r.error)console.error('deleteProposalFromDb error:',r.error);
  }

  // ── PURCHASE ORDER SUPABASE OPERATIONS ──────────────────────────────────────
  async function loadPOsFromSupabase(){
    var r=await fetchAllRows(function(){return supabase.from('purchase_orders').select('*').order('created_at',{ascending:false});});
    if(r.error){console.error('loadPOs error:',r.error);return[];}
    return(r.data||[]).map(rowToPO);
  }
  async function savePOToDb(p){
    // Mapper lives in utils.js → poToRow. Coerces non-numeric vendorId to
    // null (vendor_id is BIGINT after the 2026-05-07 migration). Schema
    // drift guard test in src/__tests__/schemaDriftGuard.test.js asserts
    // every produced field is type-compatible with the DB column.
    var row = poToRow(p);
    var lastErr=null;
    for(var attempt=1;attempt<=6;attempt++){
      try{
        var r=await supabase.from('purchase_orders').upsert(row,{onConflict:'id'});
        if(!r.error)return true;
        lastErr=r.error.message;
        if(r.error.code==='PGRST204'||lastErr.includes('does not exist')){
          var colMatch=lastErr.match(/"([^"]+)" of relation/)||lastErr.match(/column "([^"]+)"/);
          if(colMatch&&row.hasOwnProperty(colMatch[1])){
            console.warn('savePO: stripping unknown column "'+colMatch[1]+'" and retrying');
            delete row[colMatch[1]];
            continue;
          }
        }
        if(attempt<3)await new Promise(function(res){setTimeout(res,1000*attempt);});
      }catch(e){
        lastErr=e.message;
        if(attempt<3)await new Promise(function(res){setTimeout(res,1000*attempt);});
      }
    }
    console.error('savePOToDb FAILED:',lastErr);
    Sentry.captureMessage('savePOToDb failed after retries: '+lastErr,'error');
    if(window._setSaveError)window._setSaveError('\u26a0 PO save failed: '+lastErr);
    return false;
  }
  async function deletePOFromDb(id){
    var r=await supabase.from('purchase_orders').delete().eq('id',id);
    if(r.error)console.error('deletePOFromDb error:',r.error);
  }

  // ── RECEIPT SUPABASE OPERATIONS ─────────────────────────────────────────────
  async function saveReceiptToDb(receipt){
    var row=receiptToRow(receipt);
    var lastErr=null;
    for(var attempt=1;attempt<=6;attempt++){
      try{
        var r=await supabase.from('receipts').upsert(row,{onConflict:'id'});
        if(!r.error)return true;
        lastErr=r.error.message;
        // 2026-06-27 — broadened unknown-column detection to include
        // Supabase's PGRST204 "Could not find the 'X' column of 'Y' in
        // the schema cache" message (single quotes), in addition to the
        // pre-existing "X" of relation / column "X" patterns. The single-
        // quote form fires when a migration has been deployed to the JS
        // code path but not yet applied to production Postgres (schema
        // cache lag or unapplied migration). Stripping the column lets
        // the receipt save succeed without the new field; once the
        // migration is applied, subsequent saves persist the field too.
        if(r.error.code==='PGRST204'||lastErr.includes('does not exist')||lastErr.includes('schema cache')){
          var colMatch = lastErr.match(/"([^"]+)" of relation/)
            || lastErr.match(/column "([^"]+)"/)
            || lastErr.match(/the '([^']+)' column/)
            || lastErr.match(/'([^']+)' of '[^']+' in the schema cache/);
          if(colMatch&&row.hasOwnProperty(colMatch[1])){
            console.warn('saveReceipt: stripping unknown column "'+colMatch[1]+'" and retrying');
            delete row[colMatch[1]];
            continue;
          }
        }
        if(attempt<3)await new Promise(function(res){setTimeout(res,1000*attempt);});
      }catch(e){
        lastErr=e.message;
        if(attempt<3)await new Promise(function(res){setTimeout(res,1000*attempt);});
      }
    }
    console.error('saveReceiptToDb FAILED:',lastErr);
    Sentry.captureMessage('saveReceiptToDb failed after retries: '+lastErr,'error');
    if(window._setSaveError)window._setSaveError('\u26a0 Receipt save failed: '+lastErr);
    return false;
  }

  async function deleteReceiptFromDb(id){
    var r=await supabase.from('receipts').delete().eq('id',id);
    if(r.error)console.error('deleteReceiptFromDb error:',r.error);
  }

  // ── RECEIPT ATTACHMENTS (Phase 4B, 2026-06-27) ──────────────────────
  // addReceiptAttachment uploads a secondary file to Storage and inserts
  // a receipt_attachments row. Returns the saved attachment (camelCase).
  // The primary attachment for a receipt is created by the receipt's own
  // upload flow; this helper is for ADDITIONAL files (invoice + payment
  // confirmation, trip bundle airline + hotel + meals, etc.).
  //
  // Storage path: receipts/{kind}/{yyyy-mm}/{receiptId}_{attachmentId}.{ext}
  // (the underscore + attachmentId distinguishes the secondary from the
  // primary, which uses path {kind}/{yyyy-mm}/{receiptId}.{ext}).
  //
  // RLS gating: receipt_attachments_insert checks the user can edit the
  // parent receipt (owner / admin / submitter). Supabase rejects with a
  // policy violation if not authorized; we surface that as the returned
  // error message.
  async function addReceiptAttachment(receiptId, file, role) {
    if (!receiptId || !file) {
      return { ok: false, error: 'addReceiptAttachment: receiptId + file required' };
    }
    var safeRole = role || 'supporting';
    var attachmentId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : ('att-' + Date.now() + '-' + Math.floor(Math.random() * 1e6));
    // Find the parent receipt to derive a sensible Storage path prefix
    // (we use the receipt's kind so attachments live alongside their
    // primary in the same monthly folder).
    var parent = receipts.find(function(r) { return r.id === receiptId; });
    var kind = (parent && parent.kind) || 'unknown';
    var ext = (file.name.split('.').pop() || 'bin').toLowerCase().replace(/[^a-z0-9]/g, '');
    var month = new Date().toISOString().slice(0, 7);
    var path = kind + '/' + month + '/' + receiptId + '_' + attachmentId + '.' + ext;
    try {
      var up = await supabase.storage.from('receipts').upload(path, file, {
        contentType: file.type || 'application/octet-stream',
        upsert: false,
      });
      if (up.error) {
        console.error('addReceiptAttachment storage upload failed:', up.error);
        return { ok: false, error: 'Storage upload failed: ' + (up.error.message || up.error) };
      }
    } catch (e) {
      return { ok: false, error: 'Storage threw: ' + e.message };
    }
    var row = {
      id:           attachmentId,
      receipt_id:   receiptId,
      filename:     file.name,
      storage_path: path,
      mime_type:    file.type || '',
      size_bytes:   file.size || null,
      role:         safeRole,
      is_primary:   false,
      uploaded_at:  new Date().toISOString(),
      uploaded_by:  (userEmail || '').toLowerCase(),
    };
    var ins = await supabase.from('receipt_attachments').insert(row).select().single();
    if (ins.error) {
      console.error('addReceiptAttachment insert failed:', ins.error);
      // Best-effort cleanup: remove the orphaned Storage object so we
      // don't leave a file with no DB row pointing to it.
      try { await supabase.storage.from('receipts').remove([path]); } catch (_e) {}
      return { ok: false, error: 'DB insert failed: ' + ins.error.message };
    }
    var saved = rowToReceiptAttachment(ins.data);
    setReceiptAttachments(function(prev) { return (prev || []).concat([saved]); });
    return { ok: true, attachment: saved };
  }

  // updateReceiptAttachmentRole — change the role tag on an existing
  // attachment. RLS allows admins only.
  async function updateReceiptAttachmentRole(attachmentId, newRole) {
    if (!attachmentId || !newRole) {
      return { ok: false, error: 'updateReceiptAttachmentRole: id + role required' };
    }
    var r = await supabase.from('receipt_attachments')
      .update({ role: newRole })
      .eq('id', attachmentId)
      .select()
      .single();
    if (r.error) {
      console.error('updateReceiptAttachmentRole failed:', r.error);
      return { ok: false, error: r.error.message };
    }
    var saved = rowToReceiptAttachment(r.data);
    setReceiptAttachments(function(prev) {
      return (prev || []).map(function(a) { return a.id === attachmentId ? saved : a; });
    });
    return { ok: true, attachment: saved };
  }

  // deleteReceiptAttachment — removes the storage object + DB row. RLS
  // allows admins only. Refuses to delete the primary attachment (would
  // orphan the receipt's filename + storage_path mirror).
  async function deleteReceiptAttachment(attachmentId) {
    var target = (receiptAttachments || []).find(function(a) { return a.id === attachmentId; });
    if (!target) return { ok: false, error: 'Attachment not found' };
    if (target.isPrimary) {
      return { ok: false, error: 'Cannot delete the primary attachment. Mark another attachment as primary first.' };
    }
    // Storage delete first (idempotent — if the object doesn't exist
    // anymore, the remove() just returns silently).
    try {
      await supabase.storage.from('receipts').remove([target.storagePath]);
    } catch (e) {
      console.warn('deleteReceiptAttachment storage remove threw (continuing):', e.message);
    }
    var r = await supabase.from('receipt_attachments').delete().eq('id', attachmentId);
    if (r.error) {
      console.error('deleteReceiptAttachment failed:', r.error);
      return { ok: false, error: r.error.message };
    }
    setReceiptAttachments(function(prev) {
      return (prev || []).filter(function(a) { return a.id !== attachmentId; });
    });
    return { ok: true };
  }

  // ── FIRM CREDIT CARDS ─────────────────────────────────────────────────
  // Owner-only writes (RLS enforces). Optimistic local update + persist.
  async function saveCreditCard(card){
    var saved = Object.assign({}, card);
    if (!saved.id) {
      saved.id = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : 'card-' + Date.now() + '-' + Math.floor(Math.random() * 1e6);
    }
    // Snapshot the previous state so we can roll back if the upsert is
    // rejected (RLS for non-owner, CHECK for non-card method assigned to
    // a non-owner holder, etc.). Without this, a "ghost" card persists
    // in local state that doesn't exist in the DB.
    var prevSnapshot = null;
    setFirmCreditCards(function(prev){
      prevSnapshot = prev;
      var exists = prev.some(function(x){ return x.id === saved.id; });
      if (exists) return prev.map(function(x){ return x.id === saved.id ? saved : x; });
      return prev.concat([saved]);
    });
    var row = creditCardToRow(saved);
    var r = await supabase.from('firm_credit_cards').upsert(row, { onConflict: 'id' });
    if (r.error) {
      console.error('saveCreditCard error:', r.error);
      if (prevSnapshot) setFirmCreditCards(prevSnapshot);
      if (window._setSaveError) window._setSaveError('\u26a0 Card save failed: ' + r.error.message);
      return false;
    }
    return true;
  }

  async function deleteCreditCard(id){
    // Same rollback pattern -- if RLS rejects the delete, restore the
    // local row instead of leaving the user thinking it's gone.
    var prevSnapshot = null;
    setFirmCreditCards(function(prev){
      prevSnapshot = prev;
      return prev.filter(function(x){ return x.id !== id; });
    });
    var r = await supabase.from('firm_credit_cards').delete().eq('id', id);
    if (r.error) {
      console.error('deleteCreditCard error:', r.error);
      if (prevSnapshot) setFirmCreditCards(prevSnapshot);
      if (window._setSaveError) window._setSaveError('\u26a0 Card delete failed: ' + r.error.message);
    }
  }

  // Save with optimistic local update — same shape as saveProposal in ProposalsTab2.
  // Caller passes a full receipt object; we update state and persist in parallel.
  function saveReceipt(receipt){
    // Normalize: needs_link only applies to kinds that actually require a
    // link (vendor_* → PO, project_reimbursable → project). If a receipt
    // was uploaded as vendor and later re-classified as Office or unknown,
    // strand the status at needs_link is wrong — bump it to pending so the
    // approval queue isn't cluttered with non-link-needing rows.
    var kindNeedsLink = ['vendor_deposit','vendor_final','vendor_full_payment','vendor_fee','project_reimbursable']
      .indexOf(receipt.kind) >= 0;
    if (!kindNeedsLink && receipt.status === 'needs_link') {
      receipt = Object.assign({}, receipt, { status: 'pending' });
    }
    setReceipts(function(prev){
      var exists=prev.some(function(x){return x.id===receipt.id;});
      if(exists)return prev.map(function(x){return x.id===receipt.id?receipt:x;});
      return [receipt].concat(prev);  // newest first
    });
    saveReceiptToDb(receipt);
  }

  function deleteReceipt(id){
    setReceipts(function(prev){return prev.filter(function(r){return r.id!==id;});});
    deleteReceiptFromDb(id);
  }

  // ── INVOICE SUPABASE OPERATIONS ─────────────────────────────────────────────
  async function saveInvoiceToDb(invoice){
    var row=invoiceToRow(invoice);
    var lastErr=null;
    for(var attempt=1;attempt<=6;attempt++){
      try{
        var r=await supabase.from('invoices').upsert(row,{onConflict:'id'});
        if(!r.error)return true;
        lastErr=r.error.message;
        if(r.error.code==='PGRST204'||lastErr.includes('does not exist')){
          var colMatch=lastErr.match(/"([^"]+)" of relation/)||lastErr.match(/column "([^"]+)"/);
          if(colMatch&&row.hasOwnProperty(colMatch[1])){
            console.warn('saveInvoice: stripping unknown column "'+colMatch[1]+'" and retrying');
            delete row[colMatch[1]];
            continue;
          }
        }
        if(attempt<3)await new Promise(function(res){setTimeout(res,1000*attempt);});
      }catch(e){
        lastErr=e.message;
        if(attempt<3)await new Promise(function(res){setTimeout(res,1000*attempt);});
      }
    }
    console.error('saveInvoiceToDb FAILED:',lastErr);
    Sentry.captureMessage('saveInvoiceToDb failed after retries: '+lastErr,'error');
    if(window._setSaveError)window._setSaveError('\u26a0 Invoice save failed: '+lastErr);
    return false;
  }

  // Bug fix 2026-06-10 (audit Bug 3): deleting an invoice used to leave
  // stale stamps on items.final_invoice_id, items.client_deposit_invoice_id,
  // and change_orders.billed_invoice_id. The next FIN generated for those
  // items would re-pick them up via the stale stamp, double-billing risk.
  // 2026-06-07 surfaced this when a deleted FIN left items invisible to the
  // PO/FIN generator. This function now unstamps BEFORE deleting so the
  // delete is the last step — if it fails, the items are still in a clean
  // state (just orphaned from a still-existing invoice row, which a
  // subsequent delete retry resolves).
  async function deleteInvoiceFromDb(id){
    if (!id) return;
    // Unstamp items + COs that reference this invoice. Always-run both
    // queries — a DEP only matches client_deposit_invoice_id, a FIN
    // only matches final_invoice_id, but running both is cheap and
    // covers both branches without needing to query the invoice's kind
    // beforehand.
    var u1 = await supabase.from('items').update({ final_invoice_id: null }).eq('final_invoice_id', id).select('id,updated_at');
    if (u1.error) console.error('deleteInvoiceFromDb: unstamp final_invoice_id:', u1.error);
    else noteItemRevRows(u1.data); // keep this session's revs current — trigger bumped updated_at
    var u2 = await supabase.from('items').update({ client_deposit_invoice_id: null }).eq('client_deposit_invoice_id', id).select('id,updated_at');
    if (u2.error) console.error('deleteInvoiceFromDb: unstamp client_deposit_invoice_id:', u2.error);
    else noteItemRevRows(u2.data);
    var u3 = await supabase.from('change_orders').update({ billed_invoice_id: null }).eq('billed_invoice_id', id);
    if (u3.error) console.error('deleteInvoiceFromDb: unstamp billed_invoice_id:', u3.error);

    // Mirror the un-stamping in local state so consumers (PO generator
    // visibility, Orders in Process picker, Tracker) see items as
    // available immediately. Without this the user has to reload to
    // observe the cleanup.
    setItems(function(prev){
      return (prev || []).map(function(it) {
        var patch = null;
        if (it && it.finalInvoiceId === id) patch = { finalInvoiceId: null };
        if (it && it.clientDepositInvoiceId === id) {
          patch = patch ? Object.assign(patch, { clientDepositInvoiceId: null }) : { clientDepositInvoiceId: null };
        }
        return patch ? Object.assign({}, it, patch) : it;
      });
    });
    setChangeOrders(function(prev){
      return (prev || []).map(function(co) {
        if (co && co.billedInvoiceId === id) return Object.assign({}, co, { billedInvoiceId: null });
        return co;
      });
    });

    var r = await supabase.from('invoices').delete().eq('id', id);
    if (r.error) console.error('deleteInvoiceFromDb error:', r.error);
  }

  function saveInvoice(invoice){
    setInvoices(function(prev){
      var exists=prev.some(function(x){return x.id===invoice.id;});
      if(exists)return prev.map(function(x){return x.id===invoice.id?invoice:x;});
      return [invoice].concat(prev);
    });
    saveInvoiceToDb(invoice);
  }

  // Hardened: an invoice with payment allocations or that was sent in a
  // billing summary cannot be silently deleted -- doing so orphans payments
  // and breaks bookkeeping integrity. Caller must use the proper protocols
  // (Credit Memo / Refund Receipt) to reverse a paid invoice.
  // Returns { ok, error } so the UI can surface the rejection.
  async function deleteInvoice(id){
    var inv = invoices.find(function(x){ return x.id === id; });
    if (!inv) return { ok:false, error: 'Invoice not found' };
    // Hard block: invoice has payments allocated to it
    var allocs = (paymentAllocations || []).filter(function(a){
      return a.documentType === 'invoice' && a.documentId === id;
    });
    if (allocs.length > 0) {
      var totalPaid = allocs.reduce(function(s, a){ return s + (parseFloat(a.amount) || 0); }, 0);
      return { ok:false, error: 'Cannot delete invoice ' + (inv.invoiceRef || id) + ' — it has $' + totalPaid.toFixed(2) + ' in recorded payments. Reverse the payment(s) first, then delete.' };
    }
    // Hard block: invoice was sent in a billing summary (client received it)
    if (inv.status === 'paid') {
      return { ok:false, error: 'Cannot delete a paid invoice. Issue a Credit Memo or Refund Receipt to reverse, then delete.' };
    }
    if (inv.billedOnSummaryId) {
      // Name the summary properly + block only while the package is out —
      // a reopened DRAFT (the sanctioned reversal) releases the guard
      // (2026-08-03; pre-fix this named a raw uuid and never released).
      var bsr = await supabase.from('billing_summaries')
        .select('id, project_label, month, year, status, summary_type, sent_at')
        .eq('id', inv.billedOnSummaryId).maybeSingle();
      var bsRow = bsr && bsr.data;
      if (!bsRow || bsRow.status === 'sent' || bsRow.status === 'paid') {
        var bsName = bsRow
          ? (billingSummaryPeriodLabel(bsRow) + ' (' + (bsRow.project_label || '') + ')')
          : ('summary ' + inv.billedOnSummaryId);
        return { ok:false, error: 'Cannot delete invoice ' + (inv.invoiceRef || id) + ' — it went to the client in the ' + bsName + '. Reopen that summary as a draft first (Billing Summary \u2192 Reopen as draft\u2026), then delete.' };
      }
    }
    setInvoices(function(prev){ return prev.filter(function(x){ return x.id !== id; }); });
    deleteInvoiceFromDb(id);
    return { ok:true };
  }

  // ── VENDOR PAYMENT CASCADE ──────────────────────────────────────────────────
  // When a vendor receipt is approved, this writes the payment record AND
  // propagates the right side effects to every item on the PO:
  //   - vendor_deposit: starts the lead-time clock (orderPlacedDate),
  //                     advances item statuses (Deposit Paid → PO Issued),
  //                     records the vendor deposit on each item.
  //   - vendor_final:   marks vendor as paid in full on each item.
  //   - vendor_fee:     records the fee payment on the PO; does not advance status.
  // Idempotent on receipt id — if called twice for the same receipt, we won't
  // duplicate the payment row (we tag vendor_payments.reference with the
  // receipt id so we can detect re-runs).
  async function applyVendorReceiptSideEffects(receipt, opts){
    // Pure rule logic lives in utils.js — keep this function thin.
    // CC fee precedence: opts.ccFee (bookkeeper override at payment time) →
    //                    receipt.ccFee (staff entry on the receipt itself, e.g. Quinn).
    // The fee lands on its own Fee-type vendor_payments row (not on the
    // principal row's cc_fee column, which is the legacy carrier).
    //
    // CASCADE TELEMETRY: every attempt logs to console with a structured
    // prefix '[vendor-receipt-cascade]' so a future regression leaves a
    // visible trail. The 2026-05-12 → 2026-05-29 silent-failure incident
    // happened because the cascade returned {ok:false} but only logged a
    // generic console.error that nobody noticed; callers (ReceiptsTab.approve,
    // VendorPaymentsSection record-payment modal) silently swallowed the
    // failure. Returns shape now also carries `failure_at` so callers can
    // tell the user WHICH step broke (principal insert / cc fee insert /
    // item updates). See post-incident commit message for full context.
    var receiptId = receipt && receipt.id;
    var receiptKind = receipt && receipt.kind;
    console.info('[vendor-receipt-cascade] start', { receiptId: receiptId, kind: receiptKind });

    var skip = shouldApplyVendorReceiptCascade(receipt);
    if (skip) {
      console.info('[vendor-receipt-cascade] skipped', { receiptId: receiptId, reason: skip });
      return skip;
    }

    // Resolve the receipt's vendor name against the directory (name OR
    // alias, exact OR case-insensitive). Cascade requires a known
    // vendor so downstream consumers (QB push, billing roll-ups,
    // reporting) can join on a canonical record. If unresolvable,
    // emit vendor_mismatch so the UI can prompt the user to add an
    // alias on an existing vendor or create a new one (post-2026-05-30
    // aliases feature).
    var vendorName = (receipt && receipt.vendorName || '').trim();
    if (vendorName) {
      var resolved = resolveVendorByName(vendorName, vendors);
      if (!resolved) {
        var mismatchReason = 'No vendor in the directory matches "' + vendorName + '" by name or alias.';
        console.error('[vendor-receipt-cascade] failed', {
          receiptId: receiptId,
          kind: receiptKind,
          step: 'vendor_mismatch',
          reason: mismatchReason,
          receiptVendorName: vendorName,
        });
        return {
          ok: false,
          reason: mismatchReason,
          failure_at: 'vendor_mismatch',
          vendorName: vendorName,
        };
      }
    }

    var po = (purchaseOrders||[]).find(function(x){return x.id===receipt.poId;});

    var ccFeeFromOpts = opts && opts.ccFee != null ? parseFloat(opts.ccFee) : NaN;
    var ccFeeFromReceipt = parseFloat(receipt && receipt.ccFee) || 0;
    var ccFee = isFinite(ccFeeFromOpts) ? ccFeeFromOpts : ccFeeFromReceipt;

    var paymentMethod = receipt.paidWithCardId ? 'Credit card' : '';

    var insertedRows = [];

    // ── TWO-LAYER ATOMICITY MODEL (Phase 2A, 2026-06-01) ────────────
    // Step 1 below writes the principal vendor_payments row + its N
    // vendor_payment_items children ATOMICALLY via the
    // apply_vendor_payment_with_items Postgres RPC. Parent + all
    // children land together, or none of them do.
    //
    // Step 2 (CC fee row) writes a SEPARATE vendor_payments row
    // OUTSIDE the RPC. It can fail independently — the principal
    // and its children have already landed. The post-2026-05-12
    // retry-via-idempotency path handles this:
    //   - principal idempotency = (source_receipt_id, payment_type='Deposit'|'Final'|'Fee'+isCcFeeRow=false)
    //   - cc_fee   idempotency = (source_receipt_id, payment_type='Fee', isCcFeeRow=true)
    // Re-approving the receipt after a Step 2 failure finds the
    // existing principal (skips Step 1) and retries Step 2 only.
    //
    // This split is deliberate per Option P: CC fee allocation per-
    // item lives on each principal CHILD's cc_fee_share column. The
    // separate CC fee vendor_payments row carries the TOTAL — its
    // amount must equal SUM(principal children.cc_fee_share). The
    // CC fee row itself has no children of its own.
    //
    // 1. Principal row + per-item children (atomic via Phase 2A RPC).
    //    Phase 2A (2026-06-01): parent vendor_payments row AND its N
    //    vendor_payment_items children land in one transaction via the
    //    apply_vendor_payment_with_items Postgres function. If any
    //    insert fails (FK violation, type cast, missing item), the
    //    entire RPC rolls back — no half-written cascades.
    //
    //    Per-row idempotency on (source_receipt_id, payment_type) still
    //    fires before the RPC — re-runs after success skip the RPC.
    //
    //    Hard-fail on item resolution: every item_id on the PO must
    //    resolve to a row in `items`. Missing items abort the cascade
    //    BEFORE any DB write — a behavioral change from the prior
    //    soft-skip pattern. Per-item financial accounting can't tolerate
    //    silent allocation skips (would corrupt the ledger).
    //
    //    Option P (CC fee allocation): the cc_fee_share column on each
    //    principal child carries the per-item allocation of the resolved
    //    CC fee. The separate CC fee vendor_payments row (written in
    //    step 2 below) has the same total in its `amount` column. The
    //    cross-row invariant: SUM(children.cc_fee_share where
    //    payment_id = principal.id) = CC_fee_row.amount.
    var principalRow = Object.assign({}, buildVendorPaymentFromReceipt(receipt, po, {
      rowPurpose: 'principal',
      paymentMethod: paymentMethod,
    }), {
      created_at: new Date().toISOString(),
    });
    var existingPrincipal = findCascadePaymentRow(vendorPayments, receipt.id, principalRow.payment_type, false);
    if (!existingPrincipal) {
      // Pre-RPC validation: resolve every PO item_id against the catalog.
      // Missing items → hard cascade failure, no parent insert.
      var itemResolution = resolveCascadeItems(po, items);
      if (!itemResolution.ok) {
        var lookupMsg = 'Item lookup failed during cascade — PO references items not in catalog: ' + itemResolution.missingItemIds.join(', ');
        console.error('[vendor-receipt-cascade] failed', {
          receiptId: receiptId, kind: receiptKind,
          step: 'item_lookup', reason: lookupMsg,
          missingItemIds: itemResolution.missingItemIds,
        });
        return {
          ok: false,
          reason: lookupMsg,
          failure_at: 'item_lookup',
          missingItemIds: itemResolution.missingItemIds,
        };
      }

      // Compute per-item children. Two paths:
      //   (a) opts.itemAllocations provided (Phase 2B manual entry from
      //       Record Payment modal): caller has already decided the
      //       per-item shape and tagged a method ('manual_net_weighted'
      //       or 'manual_override'). The cascade NORMALIZES the caller's
      //       array into the full child row shape (fills fee_share_*
      //       cols + allocation_method) and trusts the values — the
      //       modal's UI validates sum invariants before submit.
      //   (b) Default cascade path (receipt approval): call
      //       allocateVendorPaymentToItems to net-weight-split the
      //       resolved CC fee across resolved items, tag
      //       'cascade_net_weighted'.
      // When no PO / no eligible items, principalChildren is empty —
      // RPC writes parent only (PO with no items, all Cancelled).
      var principalChildren;
      if (opts && Array.isArray(opts.itemAllocations)) {
        var manualMethod = (opts && opts.allocationMethod) || 'manual_override';
        principalChildren = opts.itemAllocations.map(function(a) {
          return {
            item_id:                a.item_id,
            amount_share:           Math.round((parseFloat(a.amount_share) || 0) * 100) / 100,
            cc_fee_share:           Math.round((parseFloat(a.cc_fee_share) || 0) * 100) / 100,
            fee_share_freight:      0,
            fee_share_crating:      0,
            fee_share_installation: 0,
            fee_share_custom:       0,
            fee_share_tariff:       0,
            allocation_method:      manualMethod,
            notes:                  '',
          };
        });
      } else {
        principalChildren = allocateVendorPaymentToItems({
          amount: principalRow.amount,
          ccFee:  ccFee,
          items:  itemResolution.items,
          allocationMethod: 'cascade_net_weighted',
        });
      }

      // Atomic write via Phase 2A RPC.
      var rpcRes = await supabase.rpc('apply_vendor_payment_with_items', {
        parent_row: principalRow,
        child_rows: principalChildren,
      });
      if (rpcRes.error) {
        console.error('[vendor-receipt-cascade] failed', {
          receiptId: receiptId, kind: receiptKind,
          step: 'principal_rpc',
          reason: rpcRes.error.message,
          childCount: principalChildren.length,
        });
        return {
          ok: false,
          reason: rpcRes.error.message,
          failure_at: 'principal_rpc',
          childCount: principalChildren.length,
        };
      }
      // RPC returns { parent, child_count }. Extract the inserted parent
      // row and update local state with the canonical row.
      var insertedParent = rpcRes.data && rpcRes.data.parent;
      if (insertedParent) {
        setVendorPayments(function(prev){ return [insertedParent].concat(prev); });
        insertedRows.push(insertedParent);
        // Phase 3 (2026-06-02): optimistically append per-item children
        // to local state so a same-session FIN build sees them. The RPC
        // returns child_count but not the rows themselves; we reconstruct
        // by stamping payment_id on the principalChildren we already
        // sent. Won't carry server-generated id/created_at/updated_at,
        // but downstream consumers (FIN CC fee aggregator) only read
        // item_id + cc_fee_share, both present.
        if (principalChildren.length > 0) {
          var optimisticChildren = principalChildren.map(function(c) {
            return Object.assign({}, c, { payment_id: insertedParent.id });
          });
          setVendorPaymentItems(function(prev){ return optimisticChildren.concat(prev); });
        }
      }
      console.info('[vendor-receipt-cascade] principal_rpc success', {
        receiptId: receiptId, kind: receiptKind,
        paymentId: insertedParent && insertedParent.id,
        childCount: (rpcRes.data && rpcRes.data.child_count) || 0,
      });
    }

    // 2. CC fee row, only for vendor_* receipts with non-zero fee. Reference
    //    carries 'CC processing fee' so the final-invoice roll-up can
    //    distinguish this from a vendor_fee receipt (which also lands as a
    //    'Fee' payment_type).
    if (ccFee > 0 && VENDOR_RECEIPT_KINDS.indexOf(receipt.kind) >= 0) {
      var ccRow = Object.assign({}, buildVendorPaymentFromReceipt(receipt, po, {
        rowPurpose: 'cc_fee',
        paymentMethod: paymentMethod,
      }), {
        amount: ccFee,                              // resolved value (opts > receipt)
        created_at: new Date().toISOString(),
      });
      var existingCcFee = findCascadePaymentRow(vendorPayments, receipt.id, 'Fee', true);
      if (!existingCcFee) {
        var ccInsert = await supabase.from('vendor_payments').insert(ccRow).select().single();
        if (ccInsert.error){
          console.error('[vendor-receipt-cascade] failed', {
            receiptId: receiptId,
            kind: receiptKind,
            step: 'cc_fee_insert',
            reason: ccInsert.error.message,
            principalPaymentId: insertedRows[0] && insertedRows[0].id,
          });
          // Principal row already landed (or pre-existed). Surface the error
          // so the UI can show it; do not roll back the principal — the
          // bookkeeper can re-run the approval to retry just the cc-fee row.
          return {
            ok:false,
            reason: ccInsert.error.message,
            failure_at: 'cc_fee_insert',
            paymentId: insertedRows[0] && insertedRows[0].id,
          };
        }
        setVendorPayments(function(prev){ return [ccInsert.data].concat(prev); });
        insertedRows.push(ccInsert.data);
      }
    }

    // 2b. PO auto-confirm (2026-07-12): a principal receipt against an
    //     ISSUED PO confirms the order — the vendor invoiced and got
    //     paid. Forward-only (Issued → Confirmed only; Draft POs keep
    //     their Issue ceremony); non-fatal — a failed save logs via
    //     savePOToDb's own alerting and never blocks the item cascade
    //     below. Pure decision in utils.poStatusPatchForReceiptCascade.
    var poStatusNext = poStatusPatchForReceiptCascade(receipt.kind, po);
    if (poStatusNext) {
      setPurchaseOrders(function(prev) {
        return prev.map(function(x) {
          return x && x.id === po.id ? Object.assign({}, x, { status: poStatusNext }) : x;
        });
      });
      savePOToDb(Object.assign({}, po, { status: poStatusNext })).then(function(ok) {
        if (!ok) console.error('[vendor-receipt-cascade] PO auto-confirm save failed', { poId: po.id, next: poStatusNext });
      });
    }

    // 3. Item cascade (orderPlacedDate stamp + Deposit Paid / PO Issued
    //    → Order Confirmed — money to the vendor commits the order,
    //    Cimon 2026-07-05). Fires for principal receipts (vendor_deposit,
    //    vendor_full_payment, vendor_final); fee-only receipts no-op.
    //    Track per-item success so callers can name the failed items in
    //    user-facing alerts (post-2026-05-29 friendly-alert pass).
    var patches = itemPatchesForReceiptCascade(receipt, po, items);
    var itemResults = await Promise.all(patches.map(async function(p){
      var ok = await updateItem(p.id, p.patch);
      return { id: p.id, ok: ok !== false };
    }));
    var failedItemRecords = itemResults.filter(function(r){ return !r.ok; });
    var failedItems = failedItemRecords.length;
    var failedItemIds = failedItemRecords.map(function(r){ return r.id; });
    if (failedItems > 0) {
      console.error('[vendor-receipt-cascade] partial failure (item updates)', {
        receiptId: receiptId,
        kind: receiptKind,
        step: 'item_updates',
        itemsFailed: failedItems,
        itemsAttempted: patches.length,
        failedItemIds: failedItemIds,
      });
      // Item updates are NOT a hard cascade failure — the principal +
      // cc_fee rows already landed. The caller still gets {ok:true} but
      // with itemsFailed > 0 + failedItemIds so the UI can name the
      // affected items in its warning alert.
      return {
        ok: true,
        paymentId:    insertedRows[0] && insertedRows[0].id,
        ccFeePaymentId: insertedRows[1] && insertedRows[1].id,
        itemsUpdated: patches.length - failedItems,
        itemsFailed:  failedItems,
        failedItemIds: failedItemIds,
        failure_at:   'item_updates',
      };
    }

    console.info('[vendor-receipt-cascade] success', {
      receiptId: receiptId,
      kind: receiptKind,
      paymentId: insertedRows[0] && insertedRows[0].id,
      ccFeePaymentId: insertedRows[1] && insertedRows[1].id,
      itemsUpdated: patches.length,
    });

    return {
      ok: true,
      paymentId:    insertedRows[0] && insertedRows[0].id,
      ccFeePaymentId: insertedRows[1] && insertedRows[1].id,
      itemsUpdated: patches.length - failedItems,
      itemsFailed:  failedItems,
    };
  }

  // ── REPLAY RECEIPT CASCADE (2026-06-07) ───────────────────────────────────
  // Re-runs applyVendorReceiptSideEffects on an already-approved receipt so
  // edits made to the receipt after approval (e.g., a CC fee that was added
  // late because of the blur-then-click race fixed in bfae063) propagate to
  // the vendor_payments + vendor_payment_items tables.
  //
  // The cascade's own idempotency (principal row + CC fee row guarded by
  // findCascadePaymentRow on source_receipt_id) handles the Fee-type row
  // insertion correctly — calling applyVendorReceiptSideEffects again on a
  // receipt whose principal exists but whose CC fee row is missing will
  // insert just the CC fee row.
  //
  // What the cascade DOESN'T do on replay: update vendor_payment_items.
  // cc_fee_share for the principal's existing children. The RPC writes
  // children only at principal-insert time (line 1273-ish skip when
  // existingPrincipal is found). For Phase 3 read consumers
  // (buildFinalInvoicePayload with usePerItemCcFee=true), the per-item
  // cc_fee_share is the source of truth — without updating it, the FIN
  // still aggregates zero CC fee even after replay successfully inserts
  // the Fee-type vendor_payments row.
  //
  // This function bridges that gap: after the cascade runs, if the receipt
  // has a non-zero cc_fee AND the principal vendor_payments row exists,
  // recompute the per-item allocation and UPDATE each child's cc_fee_share.

  // Owner correction for an already-approved receipt's RECORDED payment
  // (2026-07-13 — the Gloster deposit-vs-full mixup). The kind lock
  // exists because re-running the cascade INSERTS rows (the Hewn
  // double-count); this path is safe because it UPDATEs the existing
  // principal vendor_payments row IN PLACE — amount and payment type —
  // re-allocates the per-item shares to match, and syncs the receipt
  // row's kind + total_amount so every surface tells one story. It can
  // never record a second payment. SQL hand-correction remains the
  // fallback for shapes this refuses (multiple principals).
  async function correctRecordedPayment(receipt, corrections){
    if (!receipt || !receipt.id) return { ok:false, reason:'missing receipt' };
    var newAmount = Math.round((parseFloat(String((corrections && corrections.amount) == null ? '' : corrections.amount).replace(/[$,\s]/g, '').replace(/−/g, '-')) || 0) * 100) / 100;
    var newType = (corrections && corrections.paymentType) || '';
    if (!(newAmount > 0)) return { ok:false, reason:'the amount must be a positive number' };
    if (['Deposit','Final','Full'].indexOf(newType) < 0) return { ok:false, reason:'payment type must be Deposit, Final, or Full' };

    var refTag = 'RECEIPT:' + receipt.id;
    // State rows are RAW snake_case from the loader — read both key
    // shapes or a CC-fee row masquerades as a second principal (the
    // Future Perfect refusal, 2026-07-15).
    function vpType(vp){ return vp.paymentType || vp.payment_type || ''; }
    var forThisReceipt = (vendorPayments || []).filter(function(vp){
      if (!vp) return false;
      var srcId = vp.sourceReceiptId || vp.source_receipt_id;
      if (srcId && String(srcId) === String(receipt.id)) return true;
      return String(vp.reference || '').indexOf(refTag) >= 0;
    });
    var principals = forThisReceipt.filter(function(vp){ return vpType(vp) !== 'Fee'; });
    if (principals.length === 0) return { ok:false, reason:'no recorded payment found for this receipt' };
    if (principals.length > 1) return { ok:false, reason:'multiple payment rows found for this receipt — this one needs a hand correction' };
    var principal = principals[0];

    // The entered figure is the BANK total (what actually left the bank,
    // CC fee included). The receipt's Fee-type ledger rows stay in place;
    // the principal gets bank total − fees. Pre-2026-07-15 the entered
    // figure was written to the principal directly, so on a receipt with
    // a CC fee row, entering the bank total would have double-counted
    // the fee (13,942.08 principal + 406.08 fee = 14,348.16).
    var feeTotal = forThisReceipt.reduce(function(sum, vp){
      return vpType(vp) === 'Fee' ? sum + (parseFloat(vp.amount) || 0) : sum;
    }, 0);
    feeTotal = Math.round(feeTotal * 100) / 100;
    var newPrincipal = Math.round((newAmount - feeTotal) * 100) / 100;
    if (!(newPrincipal > 0)) {
      return { ok:false, reason:'that total is not more than the ' + feeTotal.toFixed(2) +
        ' already recorded as a CC fee for this receipt — the bank total should include the fee' };
    }
    var oldPrincipal = Math.round((parseFloat(principal.amount) || 0) * 100) / 100;
    if (Math.abs(newPrincipal - oldPrincipal) < 0.005 && vpType(principal) === newType) {
      return { ok:true, noChange:true, principal:newPrincipal, feeTotal:feeTotal, bankTotal:newAmount };
    }

    var upd = await supabase.from('vendor_payments')
      .update({ amount: newPrincipal, payment_type: newType })
      .eq('id', principal.id);
    if (upd.error) return { ok:false, reason:'payment update failed: ' + upd.error.message };

    // Re-allocate the per-item shares to the corrected amount — same
    // net-weighted split the cascade used at first write; updates
    // existing children in place, never inserts.
    var sharesUpdated = 0;
    var po = (purchaseOrders || []).find(function(pp){ return pp.id === receipt.poId; });
    if (po && Array.isArray(po.items)) {
      var poItems = (items || []).filter(function(it){ return po.items.indexOf(it.id) >= 0; });
      if (poItems.length > 0) {
        var allocation = allocateVendorPaymentToItems({
          amount: newPrincipal,
          ccFee: parseFloat(receipt.ccFee || 0) || 0,
          items: poItems,
          allocationMethod: 'manual_override',
        });
        for (var ai = 0; ai < allocation.length; ai++) {
          var a = allocation[ai];
          var cu = await supabase.from('vendor_payment_items')
            .update({ amount_share: a.amount_share, cc_fee_share: a.cc_fee_share, allocation_method: 'manual_override' })
            .eq('payment_id', principal.id)
            .eq('item_id', a.item_id);
          if (!cu.error) sharesUpdated++;
        }
        setVendorPaymentItems(function(prev){
          return (prev || []).map(function(row){
            if (!row) return row;
            var pid = row.paymentId || row.payment_id;
            if (String(pid) !== String(principal.id)) return row;
            var iid = row.itemId || row.item_id;
            var hit = allocation.find(function(x){ return String(x.item_id) === String(iid); });
            if (!hit) return row;
            return Object.assign({}, row, {
              amountShare: hit.amount_share, amount_share: hit.amount_share,
              ccFeeShare: hit.cc_fee_share, cc_fee_share: hit.cc_fee_share,
              allocationMethod: 'manual_override', allocation_method: 'manual_override',
            });
          });
        });
      }
    }

    setVendorPayments(function(prev){
      return (prev || []).map(function(vp){
        return vp && vp.id === principal.id
          ? Object.assign({}, vp, { amount: newPrincipal, paymentType: newType, payment_type: newType })
          : vp;
      });
    });

    // Sync the receipt row so the wizard, list, and statement matcher
    // all read the corrected values.
    var KIND_BY_TYPE = { Deposit: 'vendor_deposit', Final: 'vendor_final', Full: 'vendor_full_payment' };
    await saveReceipt(Object.assign({}, receipt, { kind: KIND_BY_TYPE[newType], totalAmount: newPrincipal }));

    return { ok: true, principal: newPrincipal, feeTotal: feeTotal, bankTotal: newAmount, sharesUpdated: sharesUpdated };
  }

  async function replayReceiptCascade(receipt){
    if (!receipt || !receipt.id) return { ok: false, reason: 'missing receipt' };
    if (VENDOR_RECEIPT_KINDS.indexOf(receipt.kind) < 0) {
      return { ok: false, reason: 'replay only applies to vendor-kind receipts' };
    }

    // Step 1 — call the existing cascade. Idempotency in
    // applyVendorReceiptSideEffects means this is a no-op for already-
    // landed pieces and ONLY inserts the Fee-type row if it's missing
    // and the receipt has cc_fee > 0.
    var cascadeResult = await applyVendorReceiptSideEffects(receipt);
    if (!cascadeResult || cascadeResult.ok === false) {
      return cascadeResult;  // surface failure as-is; the alert UI handles it
    }

    // Step 2 — Phase 3 cc_fee_share recompute for existing children.
    // Required when the original cascade ran with cc_fee=0 and a later
    // edit added a CC fee value; the principal row + its per-item
    // children already exist, but cc_fee_share on those children is
    // still zero.
    var ccFee = parseFloat(receipt.ccFee || 0);
    if (ccFee <= 0) {
      return Object.assign({}, cascadeResult, { ccFeeReplay: 'skipped (no cc_fee)' });
    }

    // Find this receipt's principal vendor_payments row by RECEIPT:<id>
    // tag in the reference field — same shape findCascadePaymentRow uses
    // but inlined here so we don't conflate with the cascade's lookup.
    var principalRow = (vendorPayments || []).find(function(vp) {
      if (!vp) return false;
      if ((vp.paymentType || '') === 'Fee') return false;
      var ref = String(vp.reference || '');
      return ref.indexOf('RECEIPT:' + receipt.id) >= 0;
    });
    if (!principalRow || !principalRow.id) {
      return Object.assign({}, cascadeResult, { ccFeeReplay: 'skipped (no principal — cascade should have created one)' });
    }

    // Resolve PO + its items so we can run the same net-weighted
    // allocation the cascade does at first-write time.
    var po = (purchaseOrders || []).find(function(p) { return p.id === receipt.poId; });
    if (!po || !Array.isArray(po.items)) {
      return Object.assign({}, cascadeResult, { ccFeeReplay: 'skipped (PO not resolved)' });
    }
    var poItems = (items || []).filter(function(it) {
      return po.items.indexOf(it.id) >= 0;
    });
    if (poItems.length === 0) {
      return Object.assign({}, cascadeResult, { ccFeeReplay: 'skipped (no PO items)' });
    }

    var allocation = allocateVendorPaymentToItems({
      amount: parseFloat(principalRow.amount) || 0,
      ccFee: ccFee,
      items: poItems,
      allocationMethod: 'cascade_net_weighted',
    });
    if (allocation.length === 0) {
      return Object.assign({}, cascadeResult, { ccFeeReplay: 'skipped (allocation empty)' });
    }

    // UPDATE each child's cc_fee_share. One row per item — small batch,
    // not worth bundling into a single statement.
    var updateResults = await Promise.all(allocation.map(function(a) {
      return supabase
        .from('vendor_payment_items')
        .update({ cc_fee_share: a.cc_fee_share })
        .eq('payment_id', principalRow.id)
        .eq('item_id', a.item_id);
    }));
    var failedUpdates = updateResults.filter(function(r) { return r && r.error; });
    if (failedUpdates.length > 0) {
      console.error('[replay-cc-fee-share] some UPDATE writes failed', failedUpdates);
      return Object.assign({}, cascadeResult, {
        ok: false,
        ccFeeReplay: 'partial — ' + failedUpdates.length + ' of ' + updateResults.length + ' updates failed',
        failure_at: 'cc_fee_share_update',
      });
    }

    // Refresh local React state so the FIN math + Reconciliation view
    // pick up the new cc_fee_share values without a page reload.
    if (setVendorPaymentItems) {
      setVendorPaymentItems(function(prev) {
        return (prev || []).map(function(vpi) {
          if (!vpi) return vpi;
          if (vpi.payment_id !== principalRow.id) return vpi;
          var match = allocation.find(function(a) { return a.item_id === vpi.item_id; });
          if (!match) return vpi;
          return Object.assign({}, vpi, { cc_fee_share: match.cc_fee_share });
        });
      });
    }

    return Object.assign({}, cascadeResult, {
      ccFeeReplay: 'updated ' + allocation.length + ' item(s)',
      ccFeeReplayItemsUpdated: allocation.length,
    });
  }

  // ── RETAINER LEDGER SUPABASE OPERATIONS ─────────────────────────────────────
  async function saveRetainerEntryToDb(entry){
    var row=retainerEntryToRow(entry);
    var lastErr=null;
    for(var attempt=1;attempt<=6;attempt++){
      try{
        var r;
        if(row.id){
          r=await supabase.from('retainer_ledger').upsert(row,{onConflict:'id'}).select().single();
        } else {
          delete row.id;
          r=await supabase.from('retainer_ledger').insert(row).select().single();
        }
        if(!r.error){
          if(r.data&&r.data.id&&r.data.id!==entry.id){
            var saved=rowToRetainerEntry(r.data);
            setRetainerLedger(function(prev){
              return prev.map(function(e){return e.id===entry.id?saved:e;});
            });
          }
          return true;
        }
        lastErr=r.error.message;
        if(r.error.code==='PGRST204'||lastErr.includes('does not exist')){
          var colMatch=lastErr.match(/"([^"]+)" of relation/)||lastErr.match(/column "([^"]+)"/);
          if(colMatch&&row.hasOwnProperty(colMatch[1])){
            console.warn('saveRetainerEntry: stripping unknown column "'+colMatch[1]+'" and retrying');
            delete row[colMatch[1]];
            continue;
          }
        }
        if(attempt<3)await new Promise(function(res){setTimeout(res,1000*attempt);});
      }catch(e){
        lastErr=e.message;
        if(attempt<3)await new Promise(function(res){setTimeout(res,1000*attempt);});
      }
    }
    console.error('saveRetainerEntryToDb FAILED:',lastErr);
    Sentry.captureMessage('saveRetainerEntryToDb failed after retries: '+lastErr,'error');
    if(window._setSaveError)window._setSaveError('\u26a0 Retainer entry save failed: '+lastErr);
    return false;
  }

  async function deleteRetainerEntryFromDb(id){
    var r=await supabase.from('retainer_ledger').delete().eq('id',id);
    if(r.error)console.error('deleteRetainerEntryFromDb error:',r.error);
  }

  function saveRetainerEntry(entry){
    var withId=entry.id?entry:Object.assign({},entry,{id:(crypto.randomUUID?crypto.randomUUID():(Date.now()+'-'+Math.random()).toString())});
    setRetainerLedger(function(prev){
      var exists=prev.some(function(x){return x.id===withId.id;});
      if(exists)return prev.map(function(x){return x.id===withId.id?withId:x;});
      return [withId].concat(prev);
    });
    saveRetainerEntryToDb(withId);
    return withId;
  }

  function deleteRetainerEntry(id){
    setRetainerLedger(function(prev){return prev.filter(function(e){return e.id!==id;});});
    deleteRetainerEntryFromDb(id);
  }

  // ── CREDIT LEDGER HELPERS (client + vendor share the same retry pattern) ──
  //
  // On final failure (after 6 retries), we now do three things:
  //   1. Reverse the optimistic in-memory state — the row inserted by the
  //      wrapper before this DB save fired gets removed. UI no longer
  //      lies that the save persisted.
  //   2. Surface a visible banner via window._setSaveError so the user
  //      knows immediately something failed.
  //   3. Pop a window.alert for high-stakes ledger writes (immediate,
  //      can't be missed) — finance tables can't have silent failures.
  // Earlier behavior was console.error + Sentry only; if no Sentry user,
  // failures were invisible. The two missing owner_draws on 2026-05-06
  // were almost certainly an instance of this.
  function makeLedgerSaver(table, toRow, fromRow, setter){
    return async function(entry){
      var row = toRow(entry);
      var lastErr = null;
      for(var attempt=1;attempt<=6;attempt++){
        try{
          var r;
          if(row.id){
            r = await supabase.from(table).upsert(row,{onConflict:'id'}).select().single();
          } else {
            delete row.id;
            r = await supabase.from(table).insert(row).select().single();
          }
          if(!r.error){
            if(r.data&&r.data.id&&r.data.id!==entry.id){
              var saved = fromRow(r.data);
              setter(function(prev){return prev.map(function(e){return e.id===entry.id?saved:e;});});
            }
            return true;
          }
          lastErr = r.error.message;
          if(r.error.code==='PGRST204'||lastErr.includes('does not exist')){
            var colMatch = lastErr.match(/"([^"]+)" of relation/) || lastErr.match(/column "([^"]+)"/);
            if(colMatch&&row.hasOwnProperty(colMatch[1])){
              console.warn('save '+table+': stripping unknown column "'+colMatch[1]+'"');
              delete row[colMatch[1]];
              continue;
            }
          }
          if(attempt<3) await new Promise(function(res){setTimeout(res,1000*attempt);});
        }catch(e){
          lastErr = e.message;
          if(attempt<3) await new Promise(function(res){setTimeout(res,1000*attempt);});
        }
      }
      console.error('save '+table+' FAILED:',lastErr);
      Sentry.captureMessage('save '+table+' failed: '+lastErr,'error');
      // Reverse the optimistic insert — UI must not show a row that didn't persist
      if (entry && entry.id && setter) {
        setter(function(prev){ return prev.filter(function(e){ return e.id !== entry.id; }); });
      }
      var msg = '\u26a0 ' + table + ' save failed: ' + lastErr;
      if (window._setSaveError) window._setSaveError(msg);
      // window.alert as final fallback — finance writes must never fail silently
      try { window.alert('Save failed for ' + table + '\n\n' + lastErr + '\n\nThe entry has been removed from your view because it was not persisted. Please retry or contact admin.'); } catch (e) {}
      return false;
    };
  }

  var saveClientCreditEntryToDb = makeLedgerSaver('client_credit_ledger', clientCreditToRow, rowToClientCredit, setClientCreditLedger);
  var saveVendorCreditEntryToDb = makeLedgerSaver('vendor_credit_ledger', vendorCreditToRow, rowToVendorCredit, setVendorCreditLedger);

  // Returns a Promise<entry|null>: optimistic in-memory insert + DB save.
  // Resolves to the entry on durable success, null on persistence failure.
  // Existing fire-and-forget callers can still ignore the promise; cancel
  // flows that need durability before flipping item status (CancelItemModal)
  // await it.
  function saveClientCreditEntry(entry){
    var withId = entry.id ? entry : Object.assign({}, entry, { id: (crypto.randomUUID ? crypto.randomUUID() : (Date.now()+'-'+Math.random()).toString()) });
    setClientCreditLedger(function(prev){
      var exists = prev.some(function(x){return x.id===withId.id;});
      if(exists) return prev.map(function(x){return x.id===withId.id?withId:x;});
      return [withId].concat(prev);
    });
    return saveClientCreditEntryToDb(withId).then(function(ok){ return ok ? withId : null; });
  }

  function saveVendorCreditEntry(entry){
    var withId = entry.id ? entry : Object.assign({}, entry, { id: (crypto.randomUUID ? crypto.randomUUID() : (Date.now()+'-'+Math.random()).toString()) });
    setVendorCreditLedger(function(prev){
      var exists = prev.some(function(x){return x.id===withId.id;});
      if(exists) return prev.map(function(x){return x.id===withId.id?withId:x;});
      return [withId].concat(prev);
    });
    return saveVendorCreditEntryToDb(withId).then(function(ok){ return ok ? withId : null; });
  }

  // ── SCHEDULES (rebuild Phase 1 — docs/SCHEDULES_REBUILD_SPEC.md §4.3) ──
  // Same optimistic-save + reversal contract as the ledger family.
  // Templates are open to every authenticated user by design (spec §2 #2);
  // the audience gate on field selection is the safety boundary, not a
  // role check here.
  var saveScheduleTemplateToDb = makeLedgerSaver('schedule_templates', scheduleTemplateToRow, rowToScheduleTemplate, setScheduleTemplates);
  var saveScheduleToDb = makeLedgerSaver('schedules', scheduleToRow, rowToSchedule, setSchedules);

  function saveScheduleTemplate(tpl){
    var withId = tpl.id ? tpl : Object.assign({}, tpl, { id: (crypto.randomUUID ? crypto.randomUUID() : (Date.now()+'-'+Math.random()).toString()) });
    withId = Object.assign({}, withId, { lastEditedBy: userEmail || withId.lastEditedBy || '', createdBy: withId.createdBy || userEmail || '' });
    setScheduleTemplates(function(prev){
      var exists = prev.some(function(x){return x.id===withId.id;});
      if(exists) return prev.map(function(x){return x.id===withId.id?withId:x;});
      return [withId].concat(prev);
    });
    return saveScheduleTemplateToDb(withId).then(function(ok){ return ok ? withId : null; });
  }

  function saveSchedule(sched){
    var withId = sched.id ? sched : Object.assign({}, sched, { id: (crypto.randomUUID ? crypto.randomUUID() : (Date.now()+'-'+Math.random()).toString()) });
    withId = Object.assign({}, withId, { createdBy: withId.createdBy || userEmail || '' });
    setSchedules(function(prev){
      var exists = prev.some(function(x){return x.id===withId.id;});
      if(exists) return prev.map(function(x){return x.id===withId.id?withId:x;});
      return [withId].concat(prev);
    });
    return saveScheduleToDb(withId).then(function(ok){ return ok ? withId : null; });
  }

  // ── OWNER DRAWS ──
  var saveOwnerDrawToDb = makeLedgerSaver('owner_draws', ownerDrawToRow, rowToOwnerDraw, setOwnerDraws);

  function saveOwnerDraw(draw){
    var withId = draw.id ? draw : Object.assign({}, draw, { id: (crypto.randomUUID ? crypto.randomUUID() : (Date.now()+'-'+Math.random()).toString()) });
    setOwnerDraws(function(prev){
      var exists = prev.some(function(x){return x.id===withId.id;});
      if(exists) return prev.map(function(x){return x.id===withId.id?withId:x;});
      return [withId].concat(prev);
    });
    saveOwnerDrawToDb(withId);
    return withId;
  }

  // Owner-only delete with awaited DB confirmation. Earlier version was
  // fire-and-forget so a failed delete (RLS deny, network) silently left
  // the optimistic state out of sync with the DB — and likely contributed
  // to the two missing draws found 2026-05-06.
  async function deleteOwnerDraw(id){
    if (!isOwner) {
      return { ok:false, error: 'Only the owner can delete owner draws.' };
    }
    var draw = ownerDraws.find(function(d){ return d.id === id; });
    if (!draw) return { ok:false, error: 'Owner draw not found' };
    setOwnerDraws(function(prev){ return prev.filter(function(d){ return d.id !== id; }); });
    var r = await supabase.from('owner_draws').delete().eq('id', id);
    if (r.error) {
      // Roll back optimistic delete — DB still has the row, restore in memory
      setOwnerDraws(function(prev){
        var exists = prev.some(function(d){ return d.id === id; });
        return exists ? prev : [draw].concat(prev);
      });
      console.error('deleteOwnerDraw:', r.error);
      return { ok:false, error: 'Delete failed: ' + (r.error.message || 'unknown') };
    }
    return { ok:true };
  }

  // ── STATE TAX RECORDS ──
  var saveStateTaxToDb = makeLedgerSaver('state_tax_records', stateTaxToRow, rowToStateTax, setStateTaxRecords);

  function saveStateTax(rec){
    var withId = rec.id ? rec : Object.assign({}, rec, { id: (crypto.randomUUID ? crypto.randomUUID() : (Date.now()+'-'+Math.random()).toString()) });
    setStateTaxRecords(function(prev){
      var exists = prev.some(function(x){return x.id===withId.id;});
      if(exists) return prev.map(function(x){return x.id===withId.id?withId:x;});
      return prev.concat([withId]).sort(function(a,b){return String(a.stateCode).localeCompare(String(b.stateCode));});
    });
    saveStateTaxToDb(withId);
    return withId;
  }

  async function deleteStateTax(id){
    setStateTaxRecords(function(prev){return prev.filter(function(s){return s.id!==id;});});
    var r = await supabase.from('state_tax_records').delete().eq('id',id);
    if(r.error) console.error('deleteStateTax:',r.error);
  }

  // ── SALES TAX CODES ──
  var saveSalesTaxCodeToDb = makeLedgerSaver('sales_tax_codes', salesTaxCodeToRow, rowToSalesTaxCode, setSalesTaxCodes);
  var saveSalesTaxFilingToDb = makeLedgerSaver('sales_tax_filings', salesTaxFilingToRow, rowToSalesTaxFiling, setSalesTaxFilings);

  function saveSalesTaxCode(rec){
    var withId = rec.id ? rec : Object.assign({}, rec, { id: (crypto.randomUUID ? crypto.randomUUID() : (Date.now()+'-'+Math.random()).toString()) });
    setSalesTaxCodes(function(prev){
      var exists = prev.some(function(x){return x.id===withId.id;});
      var next = exists ? prev.map(function(x){return x.id===withId.id?withId:x;}) : prev.concat([withId]);
      return next.sort(function(a,b){return String(a.code).localeCompare(String(b.code));});
    });
    saveSalesTaxCodeToDb(withId);
    return withId;
  }

  async function deleteSalesTaxCode(id){
    setSalesTaxCodes(function(prev){return prev.filter(function(s){return s.id!==id;});});
    var r = await supabase.from('sales_tax_codes').delete().eq('id',id);
    if(r.error) console.error('deleteSalesTaxCode:',r.error);
  }

  // ── SALES TAX FILINGS ──
    // Delete cascades to items + files at the DB (ON DELETE CASCADE). The
  // storage object is removed only when no OTHER filing's file row still
  // points at it — a catch-up payment shares one PDF across N filings.
  async function deleteSalesTaxFiling(id){
    var mine = salesTaxFilingFiles.filter(function(f){return f.filingId===id;});
    // DB first — a failed delete must not vanish from the screen (it would
    // reappear on reload with its stamps intact and nobody the wiser).
    var r = await supabase.from('sales_tax_filings').delete().eq('id',id);
    if(r.error){ console.error('deleteSalesTaxFiling:',r.error); return false; }
    setSalesTaxFilings(function(prev){return prev.filter(function(s){return s.id!==id;});});
    setSalesTaxFilingItems(function(prev){return prev.filter(function(x){return x.filingId!==id;});});
    setSalesTaxFilingFiles(function(prev){return prev.filter(function(x){return x.filingId!==id;});});
    // The bookkeeper's copy goes with it (DB cascade); drop its file too.
    await dropSalesTaxReceiptMirror({ filingId: id, receipts: receipts, setReceipts: setReceipts });
    var orphanPaths = mine.map(function(f){return f.storagePath;}).filter(function(path){
      return path && !salesTaxFilingFiles.some(function(o){return o.filingId!==id && o.storagePath===path;});
    });
    if(orphanPaths.length){
      try { await supabase.storage.from('sales-tax-filings').remove(orphanPaths); } catch(_e) {}
    }
    return true;
  }

  // ── commitSalesTaxFilings — the File & Record modal's save (2026-10-15).
  // Writes N filings (1 for a single period, N for a catch-up payment),
  // their stamped items, and the uploaded files, in phases that are each
  // idempotent so a failed companion write can be retried without
  // re-inserting anything (RecordClientPaymentWizard.submit pattern):
  //   1. upsert each filing by its pre-minted id (await — never fire-and-forget here)
  //   2. per filing: delete its item stamps, insert the ticked set (tax frozen)
  //   3. per pending file: upload ONCE, then one file row per filing
  //   4. remove file rows the editor dropped (storage object only when orphaned)
  // Returns {ok, filingIds, uploaded:[fileId…], error, phase}. Cimon,
  // 2026-10-15: payments are stored facts — every figure lands as a row.
  async function commitSalesTaxFilings(args){
    var entries = (args && args.filings) || [];     // [{filing, items:[{itemId, invoiceRef, taxAmount}]}]
    var newFiles = (args && args.newFiles) || [];   // [{localId, file}] (File objects)
    var removeFileIds = (args && args.removeFileIds) || [];
    var who = (userEmail || '').toLowerCase();
    var mintId = function(){ return crypto.randomUUID ? crypto.randomUUID() : (Date.now()+'-'+Math.random()).toString(); };
    var filingIds = [];
    try {
      // Phase 1 — filings.
      for (let i=0;i<entries.length;i++){
        const rec = Object.assign({}, entries[i].filing);
        if(!rec.id) rec.id = mintId();
        rec.recordedBy = rec.recordedBy || who;
        entries[i].filing = rec;
        const ok = await saveSalesTaxFilingToDb(rec);
        if(!ok) return { ok:false, phase:'filing', filingIds: filingIds, error:'The filing for '+rec.periodKey+' could not be saved. Nothing after it was written — press Save again to retry.' };
        filingIds.push(rec.id);
        setSalesTaxFilings(function(prev){
          var exists = prev.some(function(x){return x.id===rec.id;});
          return exists ? prev.map(function(x){return x.id===rec.id?rec:x;}) : prev.concat([rec]);
        });
      }
      // Phase 2 — item stamps. UPSERT the ticked set on (filing_id, item_id)
      // FIRST, then prune stamps no longer ticked — so there is never a
      // window where a recorded filing has no stamps (an edit that failed
      // between delete and insert used to leave exactly that).
      for (let j=0;j<entries.length;j++){
        const fid = entries[j].filing.id;
        const rows = (entries[j].items||[]).map(function(it){
          return salesTaxFilingItemToRow({ filingId: fid, itemId: it.itemId, invoiceRef: it.invoiceRef, taxAmount: it.taxAmount });
        });
        let saved = [];
        if(rows.length){
          const ins = await supabase.from('sales_tax_filing_items').upsert(rows, { onConflict: 'filing_id,item_id' }).select();
          if(ins.error) return { ok:false, phase:'items', filingIds: filingIds, error:'The filing IS recorded, but its items could not be stamped: '+ins.error.message+' — press Save again to retry.' };
          saved = (ins.data||[]).map(rowToSalesTaxFilingItem);
        }
        const keepIds = rows.map(function(r){return r.item_id;});
        let prune = supabase.from('sales_tax_filing_items').delete().eq('filing_id', fid);
        if(keepIds.length) prune = prune.not('item_id', 'in', '(' + keepIds.join(',') + ')');
        const del = await prune;
        if(del.error) return { ok:false, phase:'items', filingIds: filingIds, error:'The filing IS recorded and its items stamped, but an unticked item could not be un-stamped: '+del.error.message+' — press Save again to retry.' };
        setSalesTaxFilingItems(function(prev){ return prev.filter(function(x){return x.filingId!==fid;}).concat(saved); });
      }
      // Phase 3 — files: one storage object, one row per filing.
      var uploaded = [];
      var first = entries[0] && entries[0].filing;
      for (let k=0;k<newFiles.length;k++){
        const nf = newFiles[k];
        const file = nf.file;
        const fileId = nf.localId || mintId();
        const ext = ((file.name||'').split('.').pop() || 'pdf').toLowerCase().replace(/[^a-z0-9]/g,'') || 'pdf';
        const path = (first ? (first.stateCode||'XX')+'/'+(first.periodKey||'period') : 'misc') + '/' + (first ? first.id : 'x') + '_' + fileId + '.' + ext;
        // No upsert: the bucket has no UPDATE policy, and the path is unique
        // per pending file. A retry after a failed row insert re-uploads
        // because the orphan object was removed.
        let up = await supabase.storage.from('sales-tax-filings').upload(path, file, { contentType: file.type || 'application/pdf', upsert: false });
        if(up.error && /already exists|duplicate/i.test(String(up.error.message||''))) up = { error: null };
        if(up.error) return { ok:false, phase:'files', filingIds: filingIds, uploaded: uploaded, error:'The filing IS recorded and its items stamped, but '+file.name+' could not be uploaded: '+(up.error.message||up.error)+' — press Save again to retry the upload.' };
        const fileRows = entries.map(function(e){
          return { filing_id: e.filing.id, storage_path: path, filename: file.name || '', size_bytes: file.size || null, uploaded_by: who, uploaded_at: new Date().toISOString() };
        });
        const fins = await supabase.from('sales_tax_filing_files').insert(fileRows).select();
        if(fins.error){
          try { await supabase.storage.from('sales-tax-filings').remove([path]); } catch(_e) {}
          return { ok:false, phase:'files', filingIds: filingIds, uploaded: uploaded, error:'The filing IS recorded, but the file record for '+file.name+' could not be saved: '+fins.error.message+' — press Save again to retry.' };
        }
        const savedFiles = (fins.data||[]).map(rowToSalesTaxFilingFile);
        setSalesTaxFilingFiles(function(prev){ return prev.concat(savedFiles); });
        uploaded.push(nf.localId);
      }
      // Phase 4 — removed files.
      if(removeFileIds.length){
        var gone = salesTaxFilingFiles.filter(function(f){return removeFileIds.indexOf(f.id)>=0;});
        var delF = await supabase.from('sales_tax_filing_files').delete().in('id', removeFileIds);
        if(delF.error) return { ok:false, phase:'files', filingIds: filingIds, uploaded: uploaded, error:'Saved, but a removed file record could not be deleted: '+delF.error.message };
        setSalesTaxFilingFiles(function(prev){ return prev.filter(function(f){return removeFileIds.indexOf(f.id)<0;}); });
        var orphans = gone.map(function(f){return f.storagePath;}).filter(function(path){
          return path && !salesTaxFilingFiles.some(function(o){return removeFileIds.indexOf(o.id)<0 && o.storagePath===path;});
        });
        if(orphans.length){ try { await supabase.storage.from('sales-tax-filings').remove(orphans); } catch(_e) {} }
      }
      // Phase 5 — the bookkeeper's copy (2026-10-16): src/lib/salesTaxReceiptMirror.js.
      var mir = await mirrorSalesTaxReceipts({ entries: entries, newFiles: newFiles, receipts: receipts, stateTaxRecords: stateTaxRecords, salesTaxCodes: salesTaxCodes, who: who, mintId: mintId, setReceipts: setReceipts });
      if(!mir.ok) return { ok:false, phase:'receipt', filingIds: filingIds, uploaded: uploaded, error: mir.error };
      return { ok:true, filingIds: filingIds, uploaded: uploaded };
    } catch(e){
      return { ok:false, phase:'network', filingIds: filingIds, error:'Network error: '+(e&&e.message?e.message:'unknown')+(filingIds.length?' — the filing IS recorded; pressing Save again retries only what is missing.':'') };
    }
  }

  // ── STATE TAX DOCUMENTS (2026-10-16) ──────────────────────────────────
  // One storage object + one row per file. Upload first, then the row; a
  // failed row insert removes the orphan object. No optimistic row — the
  // card shows the document only once the database has it.
  async function addStateTaxDocument(rec, file, meta){
    if(!rec||!rec.id||!file) return { ok:false, error:'No record or file.' };
    var m = meta||{};
    try{
      // 2026-10-16: the first version called the filing saver's local
      // mintId here — a ReferenceError thrown BEFORE this try, so the card
      // sat on "Uploading…" forever. Everything now lives inside the try.
      var ext = ((file.name||'').split('.').pop()||'pdf').toLowerCase().replace(/[^a-z0-9]/g,'')||'pdf';
      var uid = (typeof crypto!=='undefined' && crypto.randomUUID) ? crypto.randomUUID() : (Date.now()+'-'+Math.random()).toString();
      var path = (rec.stateCode||'XX') + (rec.jurisdiction ? '-' + String(rec.jurisdiction).replace(/[^A-Za-z0-9]/g,'') : '') + '/' + rec.id + '_' + uid + '.' + ext;
      var up = await supabase.storage.from('state-tax-documents').upload(path, file, { contentType: file.type||'application/pdf', upsert:false });
      if(up.error) return { ok:false, error:(file.name||'The file')+' could not be uploaded: '+(up.error.message||up.error) };
      var row = { state_tax_record_id: rec.id, kind: m.kind||'other', label: m.label||'', expires_on: m.expiresOn||null,
        storage_path: path, filename: file.name||'', size_bytes: file.size||null, uploaded_by: (userEmail||'').toLowerCase(), uploaded_at: new Date().toISOString() };
      var ins = await supabase.from('state_tax_documents').insert(row).select().single();
      if(ins.error){
        try { await supabase.storage.from('state-tax-documents').remove([path]); } catch(_e) {}
        return { ok:false, error:'The file uploaded but its record could not be saved: '+ins.error.message };
      }
      var doc = rowToStateTaxDocument(ins.data);
      setStateTaxDocuments(function(prev){ return prev.concat([doc]); });
      return { ok:true, doc:doc };
    }catch(e){ return { ok:false, error:'Network error: '+(e&&e.message?e.message:'unknown') }; }
  }
  async function deleteStateTaxDocument(doc){
    if(!doc||!doc.id) return false;
    var del = await supabase.from('state_tax_documents').delete().eq('id', doc.id);
    if(del.error){ console.error('deleteStateTaxDocument:', del.error); return false; }
    setStateTaxDocuments(function(prev){ return prev.filter(function(d){return d.id!==doc.id;}); });
    if(doc.storagePath){ try { await supabase.storage.from('state-tax-documents').remove([doc.storagePath]); } catch(_e) {} }
    return true;
  }
  async function stateTaxDocumentUrl(storagePath){
    if(!storagePath) return '';
    var r = await supabase.storage.from('state-tax-documents').createSignedUrl(storagePath, 600);
    if(r.error){ console.error('stateTaxDocumentUrl:', r.error); return ''; }
    return (r.data && r.data.signedUrl) || '';
  }

  // Signed URL for a filed return (private bucket). 10 minutes.
  async function salesTaxFilingFileUrl(storagePath){
    if(!storagePath) return '';
    var r = await supabase.storage.from('sales-tax-filings').createSignedUrl(storagePath, 600);
    if(r.error){ console.error('salesTaxFilingFileUrl:', r.error); return ''; }
    return (r.data && r.data.signedUrl) || '';
  }

  // ── CLIENT PAYMENTS ──────────────────────────────────────────────────────
  // recordClientPayment: insert one client_payment + N payment_allocations.
  // Single source of truth for "money in" — replaces the per-document
  // payment_log JSONB pattern.
  //
  // payment: { clientId, projectLabel, paymentDate, amountTotal, method,
  //            bankReference, reference, notes, recordedBy }
  // allocations: array of { documentType, documentId, amount }
  //
  // Returns { ok, payment, allocations } or { ok: false, error }.
  async function recordClientPayment(payment, allocations){
    var allocs = allocations || [];
    var paymentRow = clientPaymentToRow(payment);
    delete paymentRow.id;
    var pr = await supabase.from('client_payments').insert(paymentRow).select().single();
    if(pr.error){ console.error('recordClientPayment insert payment:',pr.error); return { ok:false, error:pr.error }; }
    var savedPayment = rowToClientPayment(pr.data);

    var allocRows = allocs.map(function(a){
      var row = paymentAllocationToRow(Object.assign({}, a, { paymentId: savedPayment.id }));
      delete row.id;
      return row;
    });
    var savedAllocs = [];
    if(allocRows.length > 0){
      var ar = await supabase.from('payment_allocations').insert(allocRows).select();
      if(ar.error){
        console.error('recordClientPayment insert allocations:',ar.error);
        // Roll back the payment row to keep the books consistent
        await supabase.from('client_payments').delete().eq('id', savedPayment.id);
        // Surface the overflow guard's message in plain language so the
        // bookkeeper knows another tab/user already allocated against
        // the same document. Migration: payment_allocations_overflow_guard.
        var msg = ar.error.message || '';
        if (ar.error.code === '23514' || msg.indexOf('Allocation overflow') >= 0) {
          return { ok:false, error: { message: 'Allocation exceeds the document\'s remaining balance — another payment may have been recorded against the same invoice. Reload and try again.' } };
        }
        return { ok:false, error:ar.error };
      }
      savedAllocs = (ar.data||[]).map(rowToPaymentAllocation);
    }

    setClientPayments(function(prev){ return [savedPayment].concat(prev); });
    setPaymentAllocations(function(prev){ return savedAllocs.concat(prev); });
    return { ok:true, payment:savedPayment, allocations:savedAllocs };
  }

  // Remove an allocation (e.g. user clicks × on a payment entry in a doc).
  // Cascades: if the parent client_payment now has zero allocations remaining,
  // delete the payment row too. The sibling check queries the DB so it works
  // correctly when called sequentially (e.g. unpay's await loop) — the React
  // state closure would still show the original snapshot on every iteration.
  async function removePaymentAllocation(allocationId){
    var alloc = paymentAllocations.find(function(a){ return a.id === allocationId; });
    if(!alloc) return;
    setPaymentAllocations(function(prev){ return prev.filter(function(a){ return a.id !== allocationId; }); });
    var r = await supabase.from('payment_allocations').delete().eq('id', allocationId);
    if(r.error){
      console.error('removePaymentAllocation:',r.error);
      Sentry.captureMessage('removePaymentAllocation failed: ' + r.error.message, 'error');
      return;
    }

    // DB-truth sibling check: count remaining allocations against this payment.
    var sib = await supabase.from('payment_allocations')
      .select('id', { count:'exact', head:true })
      .eq('payment_id', alloc.paymentId);
    var siblingCount = sib.count == null ? null : sib.count;
    if(siblingCount === 0){
      setClientPayments(function(prev){ return prev.filter(function(p){ return p.id !== alloc.paymentId; }); });
      var rp = await supabase.from('client_payments').delete().eq('id', alloc.paymentId);
      if(rp.error){
        console.error('removePaymentAllocation (cascade payment):',rp.error);
        Sentry.captureMessage('removePaymentAllocation cascade failed: ' + rp.error.message, 'error');
      }
    }
  }

  // Edit an existing single-allocation payment recorded against one document
  // (the typical PaymentsPanel case). Updates client_payments fields +
  // the allocation's amount, then refreshes local state.
  // Multi-allocation payments (BankDepositModal) aren't editable here —
  // those need to be removed and re-recorded to keep the sum consistent.
  //
  // patch: { amount, date, method, reference, bankReference, notes }
  async function updateClientPayment(paymentId, allocationId, patch){
    if(!paymentId || !allocationId) return { ok:false, error: { message: 'paymentId + allocationId required' } };
    var sib = await supabase.from('payment_allocations')
      .select('id', { count:'exact', head:true })
      .eq('payment_id', paymentId);
    if(sib.count != null && sib.count > 1){
      return { ok:false, error: { message: 'This payment has multiple allocations — remove and re-record to edit.' } };
    }
    var amt = parseFloat(patch.amount);
    if(isNaN(amt) || amt <= 0){
      return { ok:false, error: { message: 'Amount must be > 0' } };
    }
    var pr = await supabase.from('client_payments').update({
      amount_total:   amt,
      payment_date:   patch.date || new Date().toISOString().slice(0,10),
      method:         patch.method || '',
      reference:      patch.reference || '',
      bank_reference: patch.bankReference || '',
      notes:          patch.notes || '',
    }).eq('id', paymentId).select().single();
    if(pr.error){ return { ok:false, error: pr.error }; }
    var ar = await supabase.from('payment_allocations').update({ amount: amt }).eq('id', allocationId).select().single();
    if(ar.error){ return { ok:false, error: ar.error }; }
    var savedPayment = rowToClientPayment(pr.data);
    var savedAlloc   = rowToPaymentAllocation(ar.data);
    setClientPayments(function(prev){ return prev.map(function(p){ return p.id === paymentId ? savedPayment : p; }); });
    setPaymentAllocations(function(prev){ return prev.map(function(a){ return a.id === allocationId ? savedAlloc : a; }); });
    return { ok:true, payment: savedPayment, allocation: savedAlloc };
  }

  // ── CHANGE ORDERS ────────────────────────────────────────────────────────
  // File a CO (status='pending'). Caller should have validated via
  // validateChangeOrder() before calling.
  async function fileChangeOrder(co){
    // B10 fix: every itemId in field_changes must resolve to the same client.
    // changeOrderClientIds is in utils.js so it's unit-testable. Two clients
    // means split into per-client COs (otherwise the co_credit ledger write
    // at approval time would silently mis-route to the first item's client).
    var distinctClients = changeOrderClientIds(co, items, projects);
    if (distinctClients.length > 1) {
      return { ok:false, error: 'Change Order spans ' + distinctClients.length + ' clients. File one CO per client so credits route correctly.' };
    }

    var row = changeOrderToRow(Object.assign({}, co, { status: 'pending', filedBy: userEmail }));
    delete row.id;
    var r = await supabase.from('change_orders').insert(row).select().single();
    if(r.error){ console.error('fileChangeOrder:', r.error); return { ok:false, error:r.error }; }
    var saved = rowToChangeOrder(r.data);
    setChangeOrders(function(prev){ return [saved].concat(prev); });
    return { ok:true, changeOrder:saved };
  }

  // Approve a pending CO. Applies the field_changes to the affected items
  // (one updateItem call per item in the CO), flips CO status to 'approved'
  // with approver stamp. Owner-only per the RLS policy.
  //
  // accountingImpact is the owner's call: true = roll delta into client
  // billing (final invoice / QB push); false = audit only, no QB. delta
  // is still computed and stored either way so reporting can see the
  // dollar effect even on spec-only approvals (e.g. owner absorbed a
  // vendor cost increase). See migration 20260505_change_orders_accounting_impact.
  //
  // Returns the updated CO record on success.
  async function approveChangeOrder(coId, accountingImpact){
    if (typeof accountingImpact !== 'boolean') {
      return { ok:false, error: 'approveChangeOrder requires an explicit accountingImpact boolean (spec-only vs accounting).' };
    }
    var co = changeOrders.find(function(x){ return x.id === coId; });
    if(!co) return { ok:false, error: 'CO not found' };
    if(co.status !== 'pending') return { ok:false, error: 'CO is not pending (status=' + co.status + ')' };
    if(!isOwner) return { ok:false, error: 'Only the owner can approve a Change Order' };

    // Group field changes by itemId, apply each item's combined patch.
    // Verify every referenced item still exists before applying — a CO
    // filed against a since-deleted item would otherwise silently no-op
    // and still be marked approved.
    var byItem = {};
    // Numeric material fields (netPrice, qty, markupPct) must be coerced
    // to actual numbers before save. The CO modal stringifies form values,
    // so without this `updateItem({netPrice:'1200'})` would write a string.
    // All current readers parseFloat defensively, but a future numeric
    // comparison (`item.netPrice > 1000`) would silently misbehave.
    //
    // Fix C Phase 2 (2026-05-27): salesTax DELIBERATELY NOT on this list
    // and DELIBERATELY NOT in MATERIAL_FIELD_LABELS in utils.js. Sales tax
    // is no longer a per-item stored value — it derives at document-
    // generation time from project-level config via computeItemTax. A CO
    // that changes price/qty automatically flows through to fresh tax on
    // the next invoice draw without touching fees.salesTax. If a future
    // agent needs to expose tax editing in the CO modal, that's a project-
    // settings flow (Phase 3), not a CO field.
    var NUMERIC_FIELDS = { netPrice: true, qty: true, markupPct: true };
    (co.fieldChanges || []).forEach(function(c){
      if(!byItem[c.itemId]) byItem[c.itemId] = {};
      var v = c.after;
      if (NUMERIC_FIELDS[c.field]) {
        var n = parseFloat(String(v == null ? '' : v).replace(/[$,\s]/g, ''));
        v = isNaN(n) ? 0 : n;
      }
      byItem[c.itemId][c.field] = v;
    });
    var itemIds = Object.keys(byItem);
    var missing = itemIds.filter(function(iid){
      return !items.find(function(it){ return it.id === iid; });
    });
    if(missing.length > 0){
      return { ok:false, error: 'Cannot approve — referenced item(s) no longer exist: ' + missing.join(', ') };
    }

    // Apply patches sequentially. If ANY updateItem returns false (DB save
    // failed after all retries), abort BEFORE flipping the CO to approved
    // so the audit trail doesn't lie about a half-applied change. The
    // already-applied items roll back to their previous DB state on next
    // page load since the optimistic in-memory update is not persisted
    // by failing saves.
    var failed = [];
    for(var i=0; i<itemIds.length; i++){
      var iid = itemIds[i];
      var patch = byItem[iid];
      if(typeof updateItem === 'function'){
        // eslint-disable-next-line no-await-in-loop
        var ok = await updateItem(iid, patch);
        if(ok === false) failed.push(iid);
      }
    }
    if(failed.length > 0){
      return { ok:false, error: 'Item save failed for: ' + failed.join(', ') + '. Change Order not approved. Try again or check console for details.' };
    }

    // Compute the dollar delta (post-CO vs pre-CO). Pure function in
    // utils.js so it can be unit-tested with edge cases (multi-field
    // changes, interaction terms, etc.) — see changeOrderDelta.test.js.
    var deltaAmount = calcChangeOrderDelta(co, items);

    var nowIso = new Date().toISOString();

    // Tier 1.3 + B4/B5/B11 fix: for negative-delta accounting COs, write
    // the co_credit ledger entry FIRST. If the ledger write fails, the
    // CO is NOT stamped approved — owner sees the error and can retry
    // (or reject). This avoids the silent-money-leak failure mode where
    // an approved CO carries a negative delta but no credit row exists
    // for the client to draw down later.
    //
    // Idempotency: dedupe by the entry's reference tag ('CO <ref> —
    // credit') against the loaded ledger, then insert WITHOUT an id so
    // Postgres generates the uuid. (2026-07-17: the previous synthetic
    // text id 'co_credit_<coId>' was rejected by the uuid column —
    // 'invalid input syntax for type uuid' — blocking EVERY
    // negative-delta accounting approval, surfaced on 26HOB-CO-018.)
    // The ledger is boot-loaded, so the reference check also covers a
    // retry in a later session after a partial failure.
    //
    // Positive-delta accounting COs DON'T write to the ledger here —
    // they sit on the CO with billed_invoice_id NULL until a final
    // invoice picks them up.
    if (accountingImpact && deltaAmount < 0) {
      var firstItemId = (co.fieldChanges || [])[0] && (co.fieldChanges || [])[0].itemId;
      var firstItem = items.find(function(it){ return it.id === firstItemId; }) || {};
      var project = projects.find(function(pr){ return pr.label === (firstItem.projectLabel || firstItem.project); });
      var clientId = project && project.clientId;
      if (!clientId) {
        return { ok:false, error: 'Cannot resolve clientId for the affected item(s). The CO references project "' + (firstItem.projectLabel || firstItem.project || '?') + '" which has no clientId set. Update the project first, then re-approve.' };
      }
      var coRefTag = 'CO ' + (co.coRef || coId) + ' — credit';
      var existingCredit = (clientCreditLedger || []).find(function(en) {
        return en && en.entryType === 'co_credit' && en.reference === coRefTag;
      });
      var ledgerEntry = existingCredit || null;
      try {
        if (!ledgerEntry) ledgerEntry = await saveClientCreditEntry({
          clientId: clientId,
          entryDate: nowIso.slice(0, 10),
          entryType: 'co_credit',
          amount: Math.abs(deltaAmount),
          sourceItemId: firstItemId || '',
          reference: coRefTag,
          notes: co.reason || '',
          createdBy: userEmail,
        });
      } catch (e) {
        console.error('approveChangeOrder: co_credit ledger write threw:', e);
        return { ok:false, error: 'Credit ledger write failed: ' + (e && e.message ? e.message : 'unknown') + '. CO not approved. Try again.' };
      }
      if (!ledgerEntry) {
        // makeLedgerSaver returns null when DB save fails after retries.
        return { ok:false, error: 'Credit ledger write failed (DB save returned null after retries). CO not approved. Check console + try again.' };
      }
    }

    var updates = {
      status: 'approved',
      approved_by: userEmail,
      approved_at: nowIso,
      delta_amount: deltaAmount,
      accounting_impact: accountingImpact,
    };
    var r = await supabase.from('change_orders').update(updates).eq('id', coId).select().single();
    if(r.error){ console.error('approveChangeOrder:', r.error); return { ok:false, error:r.error }; }
    var saved = rowToChangeOrder(r.data);
    setChangeOrders(function(prev){ return prev.map(function(x){ return x.id === coId ? saved : x; }); });

    // Stored FIN markup follows an ACCOUNTING change order (2026-10-06).
    // The CO is the audit trail for the new cost; without this the FIN's
    // markup_product_tot / markup_fees_tot would still say what the cost
    // was at commit. Runs AFTER the CO is approved: a restatement failure
    // must not un-approve a CO whose item patches already landed — it is
    // reported in `markupRestatement` and logged for a retry. itemsRef
    // carries the just-patched items (updateItem writes it eagerly);
    // the `items` closure may still be pre-patch.
    var markupRestatement = null;
    if (accountingImpact) {
      try {
        markupRestatement = await restateCommittedFinMarkup({
          itemIds: itemIds, items: itemsRef.current || items, invoices: invoices,
          saveInvoice: saveInvoiceToDb, setInvoices: setInvoices,
        });
        if (markupRestatement.failed.length) console.error('approveChangeOrder: stored FIN markup restatement failed for', markupRestatement.failed);
      } catch (e) {
        console.error('approveChangeOrder: stored FIN markup restatement threw', e);
        markupRestatement = { restated: [], failed: ['exception'] };
      }
    }

    return { ok:true, changeOrder:saved, markupRestatement: markupRestatement };
  }

  // Stamp a single CO's billed_invoice_id, OUTSIDE the final-invoice
  // generation flow. Use this only when a CO needs manual stamping after
  // the fact (e.g., bookkeeper clicking a recovery button). The final
  // invoice flow itself MUST go through finalizeInvoiceAtomic so the
  // invoice + item stamps + CO stamps roll back together if any write
  // fails — see LIFECYCLES.md Appendix C #17 and the guard test in
  // __tests__/finalInvoiceAtomic.test.js.
  //
  // The "Standalone" suffix is the renaming guard for that invariant: if
  // a future refactor wants to call this from generate(), the new name
  // makes the wrong intent obvious at the call site.
  async function markChangeOrderBilledStandalone(coId, invoiceId){
    if (!coId || !invoiceId) return { ok:false, error: 'markChangeOrderBilledStandalone requires coId and invoiceId' };
    var r = await supabase.from('change_orders')
      .update({ billed_invoice_id: invoiceId })
      .eq('id', coId)
      .select()
      .single();
    if (r.error) {
      console.error('markChangeOrderBilledStandalone:', r.error);
      return { ok:false, error: r.error.message || String(r.error) };
    }
    var saved = rowToChangeOrder(r.data);
    setChangeOrders(function(prev){ return prev.map(function(x){ return x.id === coId ? saved : x; }); });
    return { ok:true, changeOrder: saved };
  }

  // Atomically writes an invoice + item.final_invoice_id stamps + CO
  // billed_invoice_id stamps via the finalize_invoice_atomic Postgres RPC
  // (supabase/migrations/20260509_finalize_invoice_atomic.sql). All three
  // writes succeed or all three roll back. Used by InvoicesTab.generate()
  // for the final-invoice flow.
  //
  // Why atomic: prior to this RPC, partial failure of the CO stamp left
  // the invoice saved with billed_invoice_id NULL on the affected COs —
  // the next final invoice generation for the same proposal would
  // double-bill. LIFECYCLES.md Appendix C #17, summary finding #10.
  //
  // Local in-memory state (setInvoices, setItems, setChangeOrders) is
  // updated only after the RPC returns ok:true, so the optimistic UI
  // never shows a half-applied state.
  //
  // Returns { ok:true, invoiceId } | { ok:false, error, state? }.
  // ccFeeIds (2026-09-07): vendor_payments rows whose card fee this
  // invoice bills. Stamped inside the same transaction as the invoice, so
  // a fee is never charged twice and never lost to a partial failure.
  // taxRows (2026-09-13): the builder's per-item tax snapshot
  // (payload.taxSnapshot). Written into final_invoice_items inside the same
  // transaction — the Sales Tax report files from these rows, so they must
  // never exist without the invoice or vice versa.
  async function finalizeInvoiceAtomic(invoice, itemIds, coIds, ccFeeIds, taxRows){
    if (!invoice || !invoice.id) {
      return { ok:false, error: 'finalizeInvoiceAtomic: invoice with id required' };
    }
    var row = invoiceToRow(invoice);
    var sentAt = new Date().toISOString();
    var taxItems = (taxRows || []).map(function(t){ return finTaxSnapshotRowToDb(t, invoice.id); });
    var r;
    try {
      r = await supabase.rpc('finalize_invoice_atomic', {
        p_invoice:               row,
        p_item_ids:              itemIds || [],
        p_co_ids:                coIds   || [],
        p_final_invoice_sent_at: sentAt,
        p_cc_fee_payment_ids:    ccFeeIds || [],
        p_tax_items:             taxItems,
      });
    } catch (e) {
      console.error('finalizeInvoiceAtomic transport error:', e);
      return { ok:false, error: e.message || String(e) };
    }
    if (r.error) {
      console.error('finalizeInvoiceAtomic rpc error:', r.error);
      return { ok:false, error: r.error.message || String(r.error) };
    }
    var data = r.data || {};
    if (!data.ok) {
      // Postgres EXCEPTION block — DB rolled back. Surface the structured error.
      console.error('finalizeInvoiceAtomic db error:', data.error, data.state);
      return { ok:false, error: data.error || 'finalize_invoice_atomic returned not-ok', state: data.state };
    }
    // RPC committed. The RPC's item stamps bumped updated_at server-side —
    // re-learn those revs so this session's next saves of the consumed
    // items don't false-conflict against its own commit.
    refreshItemRevs(itemIds);
    // Now apply the same updates to in-memory state.
    setInvoices(function(prev){
      var exists = prev.some(function(x){ return x.id === invoice.id; });
      if (exists) return prev.map(function(x){ return x.id === invoice.id ? invoice : x; });
      return [invoice].concat(prev);
    });
    if (itemIds && itemIds.length > 0) {
      setItems(function(prev){
        return prev.map(function(it){
          if (itemIds.indexOf(it.id) < 0) return it;
          return Object.assign({}, it, { finalInvoiceId: invoice.id, finalInvoiceSentAt: sentAt });
        });
      });
    }
    if (coIds && coIds.length > 0) {
      setChangeOrders(function(prev){
        return prev.map(function(co){
          if (coIds.indexOf(co.id) < 0) return co;
          return Object.assign({}, co, { billedInvoiceId: invoice.id });
        });
      });
    }
    if (ccFeeIds && ccFeeIds.length > 0) {
      setVendorPayments(function(prev){
        return prev.map(function(vp){
          if (!vp || ccFeeIds.indexOf(vp.id) < 0) return vp;
          // Only the still-unbilled ones flip; the RPC guards on IS NULL
          // too, so in-memory state matches what the DB actually did.
          if (vp.billedInvoiceId) return vp;
          return Object.assign({}, vp, { billedInvoiceId: invoice.id });
        });
      });
    }
    if (taxItems.length > 0) {
      // Mirror the RPC's upsert: replace this invoice's snapshot rows.
      setFinalInvoiceItems(function(prev){
        var kept = (prev || []).filter(function(x){ return x.finalInvoiceId !== invoice.id; });
        return taxItems.map(rowToFinalInvoiceItem).concat(kept);
      });
    }
    return { ok:true, invoiceId: invoice.id };
  }

  // Reject a pending CO. No item changes are applied. Owner-only.
  async function rejectChangeOrder(coId, reason){
    var co = changeOrders.find(function(x){ return x.id === coId; });
    if(!co) return { ok:false, error: 'CO not found' };
    if(co.status !== 'pending') return { ok:false, error: 'CO is not pending' };
    if(!isOwner) return { ok:false, error: 'Only the owner can reject a Change Order' };
    var nowIso = new Date().toISOString();
    var updates = { status: 'rejected', rejected_by: userEmail, rejected_at: nowIso, rejected_reason: reason || '' };
    var r = await supabase.from('change_orders').update(updates).eq('id', coId).select().single();
    if(r.error){ console.error('rejectChangeOrder:', r.error); return { ok:false, error:r.error }; }
    var saved = rowToChangeOrder(r.data);
    setChangeOrders(function(prev){ return prev.map(function(x){ return x.id === coId ? saved : x; }); });
    return { ok:true, changeOrder:saved };
  }

  // ── ITEM NUMBERING HELPERS ───────────────────────────────────────────────
  // Reads current sequence for (projectCode, roomNumber), increments it
  // atomically, and returns the new sequence number.
  // Atomic sequence dispense via the dispense_item_sequence Postgres function.
  // The previous read-then-write pattern was a race condition: parallel calls
  // for the same project/room returned duplicate sequences, causing
  // approveProposal's SKU assignment to silently lose half the items on
  // unique-constraint conflicts. See migration 20260503_atomic_item_sequence.sql.
  async function getNextItemSequence(projectCode, roomNumber) {
    var r = await supabase.rpc('dispense_item_sequence', {
      p_project_code: projectCode,
      p_room_number:  String(roomNumber),
    });
    if (r.error) {
      console.error('dispense_item_sequence error:', r.error);
      throw r.error;
    }
    return r.data;
  }

  // Atomic dispense for proposal / PO / invoice ref sequences. Replaces the
  // local-state max+1 pattern used by generateProposalRef / generatePORef /
  // generateInvoiceRef which raced when two browsers were creating docs
  // simultaneously. See migration 20260504_po_ref_unique_and_atomic_dispense.sql.
  // kind: 'P' (proposal) | 'PO' | 'INV' (deposit/final) | 'RE' (reimbursable) | 'DH' (designer hours)
  async function dispenseDocSequence(kind, projectCode) {
    if (!kind || !projectCode) return null;
    var r = await supabase.rpc('dispense_doc_sequence', {
      p_kind: kind,
      p_project_code: projectCode,
    });
    if (r.error) {
      console.error('dispense_doc_sequence error:', r.error);
      return null;
    }
    return r.data;
  }

  // Count how many components already have item_number LIKE 'parentItemNumber%'
  // to determine the next component letter index.
  async function getComponentCount(parentItemNumber) {
    var r = await supabase
      .from('items')
      .select('id', { count: 'exact', head: true })
      .like('item_number', parentItemNumber + '%')
      .is('deleted_at', null);
    return (r.count) || 0;
  }

  // ── DATA ACTIONS ────────────────────────────────────────────────────────
  // Markup rates are stamped onto the item at creation (2026-10-06):
  // project → client → firm defaults, via resolveMarkupDefaults. Stamping
  // here (not in one form) covers every creation path — ItemForm, imports,
  // assembly children, duplicates. Only blanks are filled; a rate the
  // form already set wins.
  function stampMarkupDefaults(it){
    if(!it) return;
    var proj=(projects||[]).find(function(pr){return pr&&pr.label===it.project;})||null;
    var d=resolveMarkupDefaults(proj,clients);
    if(it.markupPct==null||it.markupPct===''||isNaN(parseFloat(it.markupPct))) it.markupPct=d.markupPct;
    if(it.feeMarkupPct==null||it.feeMarkupPct===''||isNaN(parseFloat(it.feeMarkupPct))) it.feeMarkupPct=d.feeMarkupPct;
  }
  async function addItem(parent,children){
    var newItems=[parent].concat(children||[]);
    for(var i=0;i<newItems.length;i++){
      var it=newItems[i];
      stampMarkupDefaults(it);
      if(!it.id||/^\d+$/.test(String(it.id))){
        it.id=crypto.randomUUID?crypto.randomUUID():(Date.now()+'-'+Math.random()).toString();
      }
      // Item numbers (SKUs) are NOT assigned at creation. They get assigned
      // via autoAssignItemNumber when a proposal is Approved (approveProposal
      // in ProposalsTab2). Per the spec — SKUs are the QB Product/Service ID
      // and shouldn't exist until the item is financially committed.
      queueSave(it);
    }
    // Eager itemsRef update — an updateItem call in the same tick (before
    // React commits) must be able to find the just-added items.
    var refToAdd=newItems.filter(function(it){
      return !(itemsRef.current||[]).some(function(ex){return ex.id===it.id;});
    });
    if(refToAdd.length>0) itemsRef.current=(itemsRef.current||[]).concat(refToAdd);
    setItems(function(prev){
      var toAdd=newItems.filter(function(it){
        return !prev.some(function(ex){return ex.id===it.id;});
      });
      return toAdd.length>0?prev.concat(toAdd):prev;
    });
  }

  // Assign an item number to an existing item that doesn't have one yet.
  // Returns the assigned number, or null if prerequisites (projectCode, roomNumber) are missing.
  // NOTE: callers that want the SKU to land in the SAME save as a status
  // update should use computeItemNumber() instead and pass the result into
  // updateItem alongside the other fields, to avoid two separate saves
  // racing against each other.
  async function autoAssignItemNumber(item){
    var num = await computeItemNumber(item);
    if (!num) return null;
    await updateItem(item.id, { itemNumber: num });
    return num;
  }

  // Just compute the next item number; do not save. Used by approveProposal
  // so it can combine the SKU assignment with the status update into a
  // single saveItemToSupabase call per item.
  async function computeItemNumber(item){
    if(item.itemNumber) return item.itemNumber;
    var proj=(projects||[]).find(function(pr){return pr.label===item.project;});
    var projCode=proj&&proj.projectCode;
    if(!projCode||!item.roomNumber) return null;
    // parentId is the lighting-fixture+bulb pattern, separate from assemblies.
    // Assembly components do NOT have parentId set; they link via assemblyId
    // and are first-class peers for SKU purposes.
    var isComp=!!(item.parentId);
    var parentNum=null;
    if(isComp){
      var parentIt=(items||[]).find(function(p){return p.id===item.parentId;});
      parentNum=parentIt&&parentIt.itemNumber;
      if(!parentNum) return null;
    }
    try{
      return await generateItemNumber(projCode,item.roomNumber,isComp,parentNum,{
        getNextSeq:getNextItemSequence,
        getComponentCount:getComponentCount,
      });
    }catch(e){
      console.error('computeItemNumber error:', e);
      return null;
    }
  }

  // Returns Promise<boolean> — true when the DB save lands (after any
  // retries), false on permanent failure. ItemForm uses this to drive a
  // saving-state indicator that reflects DB-confirmed state, not the
  // optimistic local update. Critical in Safari where the upsert preflight
  // fails on the first attempt and retries take several seconds.
  function updateItem(id,patch){
    // Merge from itemsRef (always-fresh), NEVER from the render-closure
    // `items`. Sequential updateItem calls inside one async pass (e.g.
    // approveProposal: SKU+status save, then the clientDepositInvoiceId
    // tag) must each see the previous call's merge — the closure array
    // doesn't update until React re-renders, so merging from it built a
    // full-row save from the PRE-approval snapshot and clobbered the
    // just-saved item_number/status in the DB (2026-07-08 SKU-loss bug).
    var base=itemsRef.current&&itemsRef.current.length?itemsRef.current:items;
    var current=base.find(function(it){return it.id===id;});
    var merged=current?Object.assign({},current,patch):patch;
    // Eager ref update so the NEXT updateItem call in this tick sees it.
    itemsRef.current=(itemsRef.current||[]).map(function(it){
      return it.id===id?merged:it;
    });
    setItems(function(prev){
      return prev.map(function(it){return it.id===id?Object.assign({},it,patch):it;});
    });
    if(merged&&merged.id&&!/^\d+$/.test(String(merged.id))){
      if(current) logChange(current, merged);
      return saveItemToSupabase(merged);
    }
    return Promise.resolve(true);
  }

  function deleteItem(id){
    var item=(items||[]).find(function(it){return it.id===id;});
    var name=item?item.item:'this item';
    if(!window.confirm('Delete "'+name+'"? The item will be moved to the recycle bin.'))return;
    deleteItemFromSupabase(id);
    try{
      var backup=JSON.parse(localStorage.getItem('csid_items_backup')||'[]');
      localStorage.setItem('csid_items_backup',JSON.stringify(backup.filter(function(it){return it.id!==id&&it.parentId!==id;})));
    }catch(e){}
    setItems(function(prev){return prev.filter(function(it){return it.id!==id&&it.parentId!==id;});});
  }

  function addVendorInline(vendor){
    // Phase 1 dual-write — see CLAUDE.md "Vendor data integrity".
    // VendorPicker hands an id-less payload here; the JSON-blob world keys
    // vendors by integer id, so we assign one now (Date.now() matches the
    // convention used elsewhere in the codebase). The same value lands in
    // vendors.legacy_id so Phase 2 can resolve picker-added vendors by id.
    var withId=(typeof vendor.id==='number')?vendor:Object.assign({},vendor,{id:Date.now()});
    var inserted=false;
    setVendors(function(prev){
      var exists=prev.some(function(v){return v.name.toLowerCase()===withId.name.toLowerCase();});
      if(exists)return prev;
      inserted=true;
      return prev.concat([withId]);
    });
    if(inserted){
      var row=vendorToRow(withId);
      row.updated_at=new Date().toISOString();
      supabase.from('vendors').upsert(row,{onConflict:'legacy_id'}).then(function(r){
        if(r&&r.error) console.error('vendors-table dual-write failed',r.error);
      }).catch(function(e){ console.error('vendors-table dual-write threw',e); });
    }
  }

  function addItems(newItems){
    newItems.forEach(function(it){
      stampMarkupDefaults(it);
      if(!it.id||it.id.toString().startsWith('import-')){
        it.id=crypto.randomUUID?crypto.randomUUID():(Date.now()+'-'+Math.random()).toString();
      }
      queueSave(it);
    });
    setItems(function(prev){return prev.concat(newItems);});
  }

  function restoreData(data){
    if(data.items){
      setItems(data.items);
      // Push every restored item to Supabase — without this, the restore
      // only lives in memory and is overwritten on the next page load.
      data.items.forEach(function(it){if(it&&it.id)queueSave(it);});
    }
    // Vendors: setVendors triggers storageSet automatically via its useEffect
    // for the JSON blob. The SQL `vendors` table is NOT automatic — without
    // an explicit bulk dual-write the mirror would silently fall out of sync
    // with the restored blob until the next per-vendor edit. Phase 2 commit
    // #1 closes that gap. See CLAUDE.md "Vendor data integrity" + the
    // 2026-05-29 vendor migration audit §2 W6.
    if(data.vendors){
      setVendors(data.vendors);
      bulkDualWriteVendorRows(data.vendors);
    }
    if(data.projects)setProjects(data.projects);
    if(data.clients)setClients(data.clients);
  }

  // ── ASSEMBLY CRUD ────────────────────────────────────────────────────────
  async function addAssembly(data){
    var r=await supabase.from('assemblies').insert(data).select().single();
    if(r.error){console.error('addAssembly error:',r.error);return null;}
    setAssemblies(function(prev){return prev.concat([r.data]);});
    return r.data;
  }
  async function updateAssembly(id,patch){
    var r=await supabase.from('assemblies').update(patch).eq('id',id).select().single();
    if(r.error){console.error('updateAssembly error:',r.error);return null;}
    var updated=r.data||Object.assign({id},patch);
    setAssemblies(function(prev){return prev.map(function(a){return a.id===id?Object.assign({},a,updated):a;});});
    return updated;
  }
  async function deleteItemDirect(id){
    // Delete without window.confirm — for use in batch operations that already confirmed
    var r=await supabase.from('items').update({deleted_at:new Date().toISOString()}).eq('id',id).select('id,updated_at');
    if(r.error){console.error('deleteItemDirect error:',r.error);return;}
    noteItemRevRows(r.data);
    setItems(function(prev){return prev.filter(function(it){return it.id!==id;});});
  }
  async function deleteAssembly(id){
    var r=await supabase.from('assemblies').delete().eq('id',id);
    if(r.error){console.error('deleteAssembly error:',r.error);return;}
    setAssemblies(function(prev){return prev.filter(function(a){return a.id!==id;});});
  }

  // ── SELECTION ────────────────────────────────────────────────────────────
  function toggleSelectionMode(){
    setSelectionMode(function(prev){
      if(prev)setSelectedItemIds(new Set());
      return !prev;
    });
  }
  function toggleItemSelected(id){
    setSelectedItemIds(function(prev){var next=new Set(prev);if(next.has(id))next.delete(id);else next.add(id);return next;});
  }
  function clearSelection(){setSelectedItemIds(new Set());}
  function selectAllVisible(ids){
    setSelectedItemIds(function(prev){var next=new Set(prev);(ids||[]).forEach(function(id){next.add(id);});return next;});
  }

  // Expose queueSave for any direct callers
  async function flushSaves(){
    if(_saveTimer){clearTimeout(_saveTimer);_saveTimer=null;}
    await flushSaveQueue();
  }

  // Direct save — bypasses queue, for explicit Save button clicks
  async function saveItemDirect(item){
    if(!item||!item.id)return;
    // Also cancel any pending debounced save for this item
    delete _saveQueue[item.id];
    await saveItemToSupabase(item);
  }

  // ── DOCUMENT SENDS — Phase 5A (2026-06-24) ─────────────────────────────
  // Records a versioned send event into document_sends. Callers pass the
  // rendered payload + the storage path of the uploaded PDF; the helper
  // validates document_type, INSERTs the row via RLS-gated admin write,
  // and prepends the saved row onto local state so cover-regen + Send
  // History UI surfaces see it without a reload.
  //
  // Returns { ok: true, row } on success; { ok: false, error } on failure.
  // Sent records are conceptually immutable: there is no updateDocumentSend
  // / deleteDocumentSend by design.
  async function recordDocumentSend(args) {
    var row = buildDocumentSendInsert(Object.assign({}, args || {}, {
      createdBy: (userEmail || '').toLowerCase(),
    }));
    if (!row) return { ok: false, error: 'invalid document_send args' };
    var r = await supabase.from('document_sends').insert(row).select().single();
    if (r.error) {
      var code = String(r.error.code || '');
      var msg = String(r.error.message || '');
      if (code === 'PGRST205' || code === '42P01' || /does not exist|relation/i.test(msg)) {
        console.warn('document_sends table not found — has the migration been run? (supabase/migrations/20260624_create_document_sends.sql)');
      }
      console.error('recordDocumentSend error:', r.error);
      return { ok: false, error: r.error.message || 'insert failed' };
    }
    var saved = rowToDocumentSend(r.data);
    setDocumentSends(function(prev) { return [saved].concat(prev || []); });
    return { ok: true, row: saved };
  }

  var value={
    // Data
    items,vendors,projects,clients,firmSettings,proposals,purchaseOrders,vendorPayments,vendorPaymentItems,
    receipts,invoices,retainerLedger,clientCreditLedger,vendorCreditLedger,ownerDraws,stateTaxRecords,finalInvoiceItems,
    clientPayments,paymentAllocations,setClientPayments,setPaymentAllocations,recordClientPayment,updateClientPayment,removePaymentAllocation,
    changeOrders,fileChangeOrder,approveChangeOrder,rejectChangeOrder,markChangeOrderBilledStandalone,
    finalizeInvoiceAtomic,
    // Atomic doc-ref dispense (dispense_doc_sequence RPC). ProposalsTab2 and
    // PurchaseOrdersTab guard their calls with `if (dispenseDocSequence ...)`
    // and silently fall back to local max+1 prediction when this is absent
    // from the value object — exactly the race the RPC was built to remove
    // (LIFECYCLES Appendix A16/B2; AUDIT_UI_2026-06 S-2). Keep it exported;
    // dispenseDocSequenceExport.test.jsx guards this line.
    dispenseDocSequence,
    userEmail,isOwner,ready,saveError,
    // Save indicators
    savedI,savedV,savedP,savedC,savedFS,
    // Actions
    setItems,setVendors,setProjects,setClients,setFirmSettings,saveFirmSettingsPatch,
    setProposals,setPurchaseOrders,setVendorPayments,setVendorPaymentItems,setReceipts,setInvoices,setRetainerLedger,
    assemblies,addAssembly,updateAssembly,deleteAssembly,
    addItem,updateItem,deleteItem,deleteItemDirect,addVendorInline,addItems,restoreData,autoAssignItemNumber,computeItemNumber,
    saveProposalToDb,patchProposalInDb,deleteProposalFromDb,
    savePOToDb,deletePOFromDb,
    saveReceipt,saveReceiptToDb,deleteReceipt,deleteReceiptFromDb,
    firmCreditCards,setFirmCreditCards,saveCreditCard,deleteCreditCard,
    saveInvoice,saveInvoiceToDb,deleteInvoice,deleteInvoiceFromDb,
    saveRetainerEntry,saveRetainerEntryToDb,deleteRetainerEntry,deleteRetainerEntryFromDb,
    applyVendorReceiptSideEffects,
    replayReceiptCascade,
    correctRecordedPayment,
    saveClientCreditEntry,saveClientCreditEntryToDb,
    saveVendorCreditEntry,saveVendorCreditEntryToDb,
    setClientCreditLedger,setVendorCreditLedger,
    saveOwnerDraw,saveOwnerDrawToDb,deleteOwnerDraw,setOwnerDraws,
    saveStateTax,saveStateTaxToDb,deleteStateTax,setStateTaxRecords,
    stateTaxDocuments,addStateTaxDocument,deleteStateTaxDocument,stateTaxDocumentUrl,
    salesTaxCodes,saveSalesTaxCode,deleteSalesTaxCode,
    salesTaxFilings,deleteSalesTaxFiling,
    salesTaxFilingItems,salesTaxFilingFiles,commitSalesTaxFilings,salesTaxFilingFileUrl,
    queueSave,flushSaves,saveItemDirect,
    selectedItemIds,selectionMode,toggleSelectionMode,toggleItemSelected,clearSelection,selectAllVisible,
    // Phase 5A — versioned send history (document_sends).
    documentSends,setDocumentSends,recordDocumentSend,
    // Phase 4A — receipt_attachments state (per-file attachments).
    // Phase 4B — CRUD helpers for multi-file UX (upload secondary file,
    // change role tag, delete non-primary attachments).
    receiptAttachments,setReceiptAttachments,
    addReceiptAttachment,updateReceiptAttachmentRole,deleteReceiptAttachment,
    // Schedules rebuild Phase 1 — template + working-document state.
    scheduleTemplates,setScheduleTemplates,saveScheduleTemplate,
    schedules,setSchedules,saveSchedule,
  };

  return React.createElement(AppContext.Provider,{value:value},children);
}

export default AppContext;
